import {
  Badge,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemText,
  MenuItem,
  Popper,
  Radio,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, grey, red } from "@mui/material/colors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useReduxHook } from "../../hooks/useReduxHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";

// TEMP: hardcoded pipeline served until the pipeline endpoint is ready. Remove
// this import + the short-circuit in fetchLoanPipeline to restore live fetching.
import mockLoanDrilldown from "../../devData/mockLoanDrilldown.json";
import { nsKey } from "../../utils/storage/partnerStorage";
import {
  ACQUISITION_STATUSES,
  PIPELINES,
  PIPELINE_KEYS,
  drilldownBaseForRow,
} from "../../pipelines/pipelineConfig";

import {
  AddRounded,
  CloseRounded,
  DataObjectOutlined,
  DeleteOutlineRounded,
  DownloadOutlined,
  DownloadRounded,
  EastOutlined,
  EditRounded,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  FilterAltOutlined,
  ForkRightOutlined,
  HourglassEmptyOutlined,
  KeyboardArrowDown,
  OpenInNewRounded,
  SaveOutlined,
  StarOutlineRounded,
  StarRounded,
  SubdirectoryArrowRight,
  SwapVertOutlined,
  UpdateRounded,
  ViewComfyOutlined,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import { isBefore, format } from "date-fns";
import { isEmpty, isNil, size } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import CellRendererEditableLoanField from "../../_src_shared/agGrid/cellRenderer/CellRendererEditableLoanField";
import { columnTypesDate } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { columnTypesDollar } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import { columnTypesNumber } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import { sanitizeSavedColumnState } from "../../utils/agGrid/sanitizeColumnState";
import { getLoanBorrowerEntityName } from "../../utils/loanPipeline/getLoanBorrowerEntityName";
import CellRendererLoanComments from "../../components/agGrid/cellRenderer/CellRendererLoanComments";
import CellRendererLoanName from "../../components/agGrid/cellRenderer/CellRendererLoanName";
import CellRendererStackedChip from "../../components/agGrid/cellRenderer/CellRendererStackedChip";
import CellRendererStatusStartDate, {
  statusStartDateFilterParams,
} from "../../components/agGrid/cellRenderer/CellRendererStatusStartDate";
import ButtonMenu from "../../components/ButtonMenu";
import SearchInput from "../../components/inputs/SearchInput";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import Flx from "../../components/layout/Flx";
import ScreenContent from "../../components/layout/ScreenContent";
import BasicModal from "../../components/modals/BasicModal";
import JsonPreview from "../../components/common/JsonPreview";
import FilterTabSwitcher from "../../components/navigation/FilterTabSwitcher";
import StyledMenu from "../../components/StyledMenu";
import Txt from "../../components/typography/Txt";
import ChipItem from "../../components/ui/ChipItem";
import OpenInNewTabButton from "../../components/ui/buttons/OpenInNewTabButton";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { useUserPreferences } from "../../hooks/utils/useUserPreferences";
import CreateLoanButton from "../../components/pipelineTable/buttons/CreateLoanButton";
import SlowRequestPrompt from "../../components/SlowRequestPrompt/SlowRequestPrompt";
import { useEnv } from "../../hooks/useEnv";
import { useEnsureUsers } from "../../hooks/useEnsureReferenceData";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import {
  ink,
  level1,
  level2,
  slate,
  status as statusColors,
} from "../../theme/colors";
import colors from "../../theme/colors";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";

// ─── Constants ────────────────────────────────────────────────────────────────

// Renders an assigned user's full name, truncated with an ellipsis when it
// overflows the column. Hovering reveals the full name with their email in <>
// next to it. Used by the role columns (Loan Officer / Sales Analyst /
// Underwriter / Processor / Loan Closer). Exported so the Acquisitions column
// set (acquisitionsColumnDefs.js) can reuse the same name/tooltip resolution.
export const CellRendererRoleUser = (p) => {
  const users = useSelector((state) => state.users);
  const email = p?.value;
  if (!email) return null;
  // Resolve the matching user's full name (independent of the email) so the
  // tooltip can show both when available.
  const matchedUser = (users || []).find((u) => u?.emailAddress === email);
  const fullName =
    matchedUser?.fullName && String(matchedUser.fullName).trim()
      ? String(matchedUser.fullName).trim()
      : null;
  // When there's no full name on file, show the email itself (not "Unknown").
  // The "<email>" suffix is no longer displayed in the cell.
  const display = fullName || email;
  // Tooltip: "{fullName} <{email}>" when both are known, otherwise whichever
  // single value we have (full name preferred), or empty when neither.
  let tooltipText = "";
  if (fullName && email) tooltipText = `${fullName} <${email}>`;
  else if (fullName) tooltipText = fullName;
  else if (email) tooltipText = email;
  return (
    <Tooltip
      placement="top-start"
      title={
        tooltipText ? (
          <Box sx={{ fontSize: 12, fontWeight: 600 }}>{tooltipText}</Box>
        ) : (
          ""
        )
      }
    >
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          fontSize: "inherit",
          fontWeight: 400,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {display}
      </Box>
    </Tooltip>
  );
};

// ─── Loan Related Contacts ─────────────────────────────────────────────────────

const formatContactName = (c) =>
  [c?.firstName, c?.lastName].filter(Boolean).join(" ").trim() ||
  c?.emailAddress ||
  "Unknown";

// Pulls the loan's related contacts, optionally narrowed to a single loanRole.
const getLoanRelatedContacts = (data, roleFilter) => {
  const contacts = Array.isArray(data?.loanRelatedContacts)
    ? data.loanRelatedContacts
    : [];
  return roleFilter
    ? contacts.filter((c) => c?.loanRole === roleFilter)
    : contacts;
};

// Renders loan-related contacts as a comma-separated list of names, truncated
// with an ellipsis. Hovering reveals each contact's full name, role (when not
// already filtered to one), company, and email. Used by the Broker column
// (roleFilter="Broker Contact") and the all-contacts column (showRole).
const CellRendererLoanRelatedContacts = ({ params, roleFilter, showRole }) => {
  if (params?.node?.group) return null;
  const contacts = getLoanRelatedContacts(params?.data, roleFilter);
  if (isEmpty(contacts)) return null;
  return (
    <Tooltip
      placement="top-start"
      title={
        <Box
          sx={{
            fontSize: 12,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {contacts.map((c, i) => (
            <Box key={c?._id || c?.contactId || i}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {formatContactName(c)}
              </Box>
              {showRole && c?.loanRole ? (
                <Box component="span" sx={{ opacity: 0.8 }}>
                  {` — ${c.loanRole}`}
                </Box>
              ) : null}
              {c?.company ? (
                <Box component="span" sx={{ opacity: 0.8 }}>
                  {` (${c.company})`}
                </Box>
              ) : null}
              {c?.emailAddress ? (
                <Box component="span" sx={{ opacity: 0.7 }}>
                  {` <${c.emailAddress}>`}
                </Box>
              ) : null}
            </Box>
          ))}
        </Box>
      }
    >
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          fontSize: 11,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {contacts.map(formatContactName).join(", ")}
      </Box>
    </Tooltip>
  );
};

// ─── Milestone History ─────────────────────────────────────────────────────────

const formatMilestoneDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "" : format(d, "MMM d, yyyy");
};

const milestoneTransitionLabel = (c) =>
  `${c?.priorMilestoneStatus ?? "?"} → ${c?.updatedMilestoneStatus ?? "?"}`;

// Renders the most recent milestone transition ("Prior → Updated · date") in the
// cell; hovering reveals the full chronological timeline with dates and who made
// each change. Replaces the raw "[object Object]" the array rendered before.
const CellRendererMilestoneHistory = (p) => {
  if (p?.node?.group) return null;
  const changes = Array.isArray(p?.data?.milestoneStatusChanges)
    ? p.data.milestoneStatusChanges
    : [];
  if (isEmpty(changes)) return null;
  const latest = changes[changes.length - 1];
  const latestDate = formatMilestoneDate(latest?.milestoneChangeDate);
  return (
    <Tooltip
      placement="top-start"
      title={
        <Box
          sx={{
            fontSize: 12,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {changes.map((c, i) => (
            <Box key={i}>
              {formatMilestoneDate(c?.milestoneChangeDate) ? (
                <Box component="span" sx={{ opacity: 0.7 }}>
                  {`${formatMilestoneDate(c?.milestoneChangeDate)} · `}
                </Box>
              ) : null}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {milestoneTransitionLabel(c)}
              </Box>
              {c?.updateBy ? (
                <Box component="span" sx={{ opacity: 0.7 }}>
                  {` · ${c.updateBy}`}
                </Box>
              ) : null}
            </Box>
          ))}
        </Box>
      }
    >
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          fontSize: 11,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Box component="span" sx={{ fontWeight: 600 }}>
          {milestoneTransitionLabel(latest)}
        </Box>
        {latestDate ? (
          <Box component="span" sx={{ color: "text.secondary" }}>
            {` · ${latestDate}`}
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  );
};

// ─── Exceptions ────────────────────────────────────────────────────────────────

// Buckets over the backend's EXCEPTION_STATUS_OPTIONS (checklists.py): "Open"
// (or unset — the model default) is open; "Approved" is approved; everything
// else (the In Review variants and Rejected) is "other". The hover card still
// counts Approved + Rejected (EXCEPTION_CLEARED_STATUSES) as resolved.
const EXCEPTION_BUCKETS = [
  { key: "open", label: "Open", tone: statusColors.warning },
  { key: "approved", label: "Approved", tone: statusColors.success },
  { key: "other", label: "Other", tone: statusColors.info },
];
const EXCEPTION_CLEARED_STATUSES = ["Approved", "Rejected"];

const exceptionStatusOf = (e) => e?.exceptionStatus || "Open";

const exceptionBucketOf = (e) => {
  const s = exceptionStatusOf(e);
  if (s === "Open") return "open";
  if (s === "Approved") return "approved";
  return "other";
};

const exceptionStatusTone = (s) => {
  if (s === "Open") return statusColors.warning;
  if (s === "Approved") return statusColors.success;
  if (s === "Rejected") return statusColors.error;
  return statusColors.info;
};

const loanExceptions = (data) =>
  Array.isArray(data?.exceptions) ? data.exceptions.filter(Boolean) : [];

const countExceptionBuckets = (exceptions) =>
  exceptions.reduce(
    (acc, e) => {
      acc[exceptionBucketOf(e)] += 1;
      return acc;
    },
    { open: 0, approved: 0, other: 0 },
  );

const ExceptionStatusPill = ({ status: s }) => {
  const tone = exceptionStatusTone(s);
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        px: 0.75,
        py: "1px",
        borderRadius: "999px",
        fontSize: 10.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        bgcolor: tone.bg,
        color: tone.fg,
      }}
    >
      {s}
    </Box>
  );
};

// The hover card's tabs are one per exception status, in the backend's
// EXCEPTION_STATUS_OPTIONS order (checklists.py). Labels may shorten; the value
// stays the model's exact string.
const EXCEPTION_STATUS_ORDER = [
  "Open",
  "In Review",
  "In Review - Pending Credit Committee",
  "Approved",
  "Rejected",
];
const EXCEPTION_STATUS_LABELS = {
  "In Review - Pending Credit Committee": "Credit Committee",
};

const ExceptionItem = ({ exception: e }) => {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const descRef = useRef(null);
  const description = e?.exceptionDescription || e?.description;
  useEffect(() => {
    const el = descRef.current;
    if (el && !expanded) setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [description, expanded]);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        "& + &": { borderTop: `1px solid ${slate[200]}` },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        {description ? (
          <>
            <Box
              ref={descRef}
              component="div"
              sx={{
                fontSize: 13,
                lineHeight: 1.45,
                color: slate[700],
                whiteSpace: "pre-line",
                ...(expanded
                  ? {}
                  : {
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }),
              }}
            >
              {description}
            </Box>
            {clamped || expanded ? (
              <ButtonBase
                onClick={() => setExpanded((v) => !v)}
                sx={{
                  alignSelf: "flex-start",
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: colors.action,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {expanded ? "Show less" : "Show more"}
              </ButtonBase>
            ) : null}
          </>
        ) : (
          <Box component="span" sx={{ fontSize: 12.5, color: slate[400] }}>
            No description
          </Box>
        )}
      </Box>
      <ExceptionStatusPill status={exceptionStatusOf(e)} />
    </Box>
  );
};

// Hover card: header (resolved progress), one tab per status present plus All,
// the matching exceptions, and a footer link that opens the loan in a new tab.
const ExceptionsHoverCard = ({ loan, exceptions, onClose }) => {
  const statusCounts = exceptions.reduce((acc, e) => {
    const s = exceptionStatusOf(e);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const statuses = [
    ...EXCEPTION_STATUS_ORDER.filter((s) => statusCounts[s]),
    ...Object.keys(statusCounts).filter(
      (s) => !EXCEPTION_STATUS_ORDER.includes(s),
    ),
  ];
  const tabs = [
    ...statuses.map((s) => ({
      key: s,
      label: EXCEPTION_STATUS_LABELS[s] || s,
      count: statusCounts[s],
    })),
    { key: "all", label: "All", count: exceptions.length },
  ];
  const [tab, setTab] = useState(statusCounts.Open ? "Open" : "all");
  const shown =
    tab === "all"
      ? exceptions
      : exceptions.filter((e) => exceptionStatusOf(e) === tab);

  const resolvedCount = exceptions.filter((e) =>
    EXCEPTION_CLEARED_STATUSES.includes(exceptionStatusOf(e)),
  ).length;
  const pct = exceptions.length ? (resolvedCount / exceptions.length) * 100 : 0;

  return (
    <Box
      onClick={(ev) => ev.stopPropagation()}
      sx={{ width: 400, display: "flex", flexDirection: "column" }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 1.75, pb: 0 }}>
        <Flx ac jb sx={{ gap: 1 }}>
          <Box
            component="span"
            sx={{ fontSize: 16, fontWeight: 700, color: ink }}
          >
            Exceptions
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{ mr: -0.75 }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>
        <Flx ac sx={{ gap: 1.5, mt: 1.5 }}>
          <Box
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              bgcolor: slate[200],
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${pct}%`,
                height: "100%",
                borderRadius: 2,
                bgcolor: statusColors.success.solid,
              }}
            />
          </Box>
          <Box
            component="span"
            sx={{ fontSize: 12, color: slate[500], whiteSpace: "nowrap" }}
          >
            {`${resolvedCount} of ${exceptions.length} resolved`}
          </Box>
        </Flx>
      </Box>

      {/* Status tabs */}
      <Box
        sx={{
          display: "flex",
          gap: 2.25,
          px: 2,
          mt: 1.25,
          borderBottom: `1px solid ${slate[200]}`,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {tabs.map((t) => {
          const active = tab === t.key;
          return (
            <ButtonBase
              key={t.key}
              onClick={() => setTab(t.key)}
              sx={{
                flexShrink: 0,
                gap: 0.75,
                py: 1.25,
                fontSize: 13,
                fontWeight: active ? level2.weight : 500,
                color: active ? level2.text : slate[500],
                boxShadow: active
                  ? `inset 0 -2px 0 ${level2.indicator}`
                  : "none",
                "&:hover": { color: active ? level2.text : slate[700] },
              }}
            >
              {t.label}
              <Box
                component="span"
                sx={{
                  minWidth: 18,
                  px: 0.625,
                  borderRadius: "999px",
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: "18px",
                  textAlign: "center",
                  bgcolor: active ? level2.tint : slate[100],
                  color: active ? level2.text : slate[500],
                }}
              >
                {t.count}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>

      {/* List */}
      <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
        {shown.map((e, i) => (
          <ExceptionItem key={e?._id ?? e?.oid ?? i} exception={e} />
        ))}
      </Box>

      {/* Footer */}
      <Flx ac sx={{ px: 2, py: 1, borderTop: `1px solid ${slate[200]}` }}>
        <Button
          variant="text"
          size="small"
          endIcon={<OpenInNewRounded sx={{ fontSize: "16px !important" }} />}
          onClick={() => {
            onClose();
            window.open(
              `${drilldownBaseForRow(loan)}/${loan?._id}`,
              "_blank",
              "noopener",
            );
          }}
          sx={{ px: 0.5, minWidth: 0 }}
        >
          Open loan file
        </Button>
      </Flx>
    </Box>
  );
};

// Cell shows open / approved / other counts; hover opens a card listing every
// exception by status. Replaces the raw "[object Object]" the array rendered
// before.
const CellRendererExceptions = (p) => {
  const [open, setOpen] = useState(false);
  if (p?.node?.group) return null;
  const exceptions = loanExceptions(p?.data);
  if (isEmpty(exceptions)) return null;
  const counts = countExceptionBuckets(exceptions);
  return (
    <Tooltip
      // The theme defaults every Tooltip to `disableInteractive` + `arrow`; this
      // card has tabs and buttons, so it must stay open while the pointer is in it.
      disableInteractive={false}
      arrow={false}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      placement="bottom-start"
      enterDelay={250}
      leaveDelay={150}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: "none",
            p: 0,
            bgcolor: "#fff",
            color: ink,
            border: `1px solid ${slate[200]}`,
            borderRadius: "12px",
            boxShadow: "0 12px 32px rgba(15,23,42,0.16)",
            overflow: "hidden",
            // Close the default 14px gap so the pointer can cross into the card.
            marginTop: "2px !important",
          },
        },
      }}
      title={
        <ExceptionsHoverCard
          loan={p?.data}
          exceptions={exceptions}
          onClose={() => setOpen(false)}
        />
      }
    >
      {/* Box, not Flx: Tooltip needs a ref-forwarding child to attach hover. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          gap: 0.5,
          minWidth: 0,
        }}
      >
        {EXCEPTION_BUCKETS.map((b) => (
          <Box
            key={b.key}
            component="span"
            sx={{
              px: 0.75,
              py: "1px",
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
              lineHeight: 1.6,
              bgcolor: counts[b.key] ? b.tone.bg : slate[50],
              color: counts[b.key] ? b.tone.fg : slate[400],
            }}
          >
            {`${counts[b.key]} ${b.label}`}
          </Box>
        ))}
      </Box>
    </Tooltip>
  );
};

// First-visit hint shown next to the Compact toggle. Dismissable; the parent
// remembers the dismissal in localStorage.
const CompactModeHintPopper = ({ anchorEl, open, onClose, onDismiss }) => {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300 }}
      modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
    >
      <Box
        sx={{
          position: "relative",
          maxWidth: 320,
          p: 1.5,
          background: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "10px",
          boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
        }}
      >
        {/* Temporary close — hides for this session only (does NOT persist like
            "Don't show again"). */}
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", top: 4, right: 4, p: 0.25 }}
        >
          <CloseRounded sx={{ fontSize: 16 }} />
        </IconButton>
        <Txt sx={{ fontWeight: 700, fontSize: 13, mb: 0.25, pr: 2.5 }}>
          ✨ New: Compact mode
        </Txt>
        <Txt secondary sx={{ fontSize: 12, mb: 1 }}>
          Condense rows to fit more loans on screen — hides chips/badges and
          shrinks comments.
        </Txt>
        <Txt secondary sx={{ fontSize: 12, mb: 1, fontStyle: "italic" }}>
          Toggle it anytime, your preference will be remembered next time you
          visit
        </Txt>
        <Flx jc="flex-end">
          <Button size="small" variant="text" onClick={onDismiss}>
            Don't show again
          </Button>
        </Flx>
      </Box>
    </Popper>
  );
};

const PIPELINE_NAME_TO_TITLE_MAP = {
  all: "All Loans",
  underwriting: "Underwriting Pipeline",
  scenarios: "Priced Scenarios",
  "borrower-portal-scenarios": "Borrower Portal Scenarios Pipeline",
  // Header workspace tabs — no dedicated screen yet, so they fall back to the
  // full loan pipeline (treated like "all"). Registering them here keeps the
  // unknown-name redirect (→ /loans/all) from bouncing the active tab.
  "pre-close": "Pre-Close",
  closing: "Closing",
  "post-close": "Post Close",
  "loan-lock-requests": "Lock Requests",
};

// Header workspace-tab slugs that should show the full pipeline (like "all").
const FALLBACK_ALL_PIPELINE_NAMES = [
  "pre-close",
  "closing",
  "post-close",
  "loan-lock-requests",
];
const LOAN_STATUS_OPTIONS = [
  "Prospect",
  "Terms Accepted",
  "Processing",
  "Underwriting",
  "Condition Clearance",
  "Final Review",
  "Clear To Close",
  "Closed",
  "Inactive",
  "Dead",
];

export const LOAN_SUBSTATUS_MAP = {
  Processing: ["Appraisal Ordered", "Appraisal In", "Pre-Underwrite"],
  "Clear To Close": ["Docs Out", "Docs Back"],
  Closed: ["Funded with Post Close Conditions", "Funded"],
};

const SUBSTATUS_TO_PARENT_MAP = Object.entries(LOAN_SUBSTATUS_MAP).reduce(
  (acc, [parent, subs]) => {
    subs.forEach((sub) => {
      acc[sub] = parent;
    });
    return acc;
  },
  {},
);

// ─── Row-group ordering for Loan Status ──────────────────────────────────────
// Grouping by Loan Status otherwise stacks the groups in whatever order the
// rows happened to arrive in (AG Grid doesn't derive group order from row
// order), which reads as random: Prospect, Processing, Underwriting, Terms
// Accepted, … Force the drilldown progress-bar order instead.
//
// LOAN_STATUS_OPTIONS above IS that ladder: its first seven entries are exactly
// the `STAGES` keys that LoanStageProgressBar and the Summary StageTracker
// render (summaryShared.js), followed by the terminal states. Keep the two in
// step — if a milestone is ever added to STAGES, add it here as well.
//
// The terminal tail (Closed → Inactive → Dead) is our call, not the bar's:
// STAGES deliberately stops at Clear To Close because those three take a loan
// off the progression. Closed sits first as the happy-path end, then the
// off-track pair.
//
// Acquisitions follow their own ladder: ACQUISITION_STATUSES is the trade
// lifecycle in stage-ribbon order (New Trade → … → Boarded, the
// ACQUISITION_STAGES keys in summaryShared.js), then the off-track Kicked /
// Declined and the on-hold Inactive. It has no sub-statuses.
const STATUS_GROUP_LADDERS = {
  [PIPELINE_KEYS.UNDERWRITING]: {
    statuses: LOAN_STATUS_OPTIONS,
    subStatuses: LOAN_SUBSTATUS_MAP,
  },
  [PIPELINE_KEYS.ACQUISITIONS]: {
    statuses: ACQUISITION_STATUSES,
    subStatuses: {},
  },
};

const statusGroupRank = (groupKey, ladder) => {
  // The Loan Status value folds the sub-status in ("Clear To Close > Docs Out")
  // when the Sub Status column is hidden, so a group key can be composite.
  // Rank on the parent status, then on the sub-status within that parent.
  const [parent, sub] = String(groupKey ?? "")
    .split(">")
    .map((part) => part.trim());
  const parentRank = ladder.statuses.indexOf(parent);
  const subRank = (ladder.subStatuses[parent] || []).indexOf(sub);
  return [
    // Unknown statuses sort to the bottom rather than jumping the ladder.
    parentRank === -1 ? Number.MAX_SAFE_INTEGER : parentRank,
    // A bare parent ("Closed") precedes its sub-status groups ("Closed > …").
    sub ? (subRank === -1 ? Number.MAX_SAFE_INTEGER : subRank) : -1,
  ];
};

// Which pipeline a status group belongs to. Mixed boards (the Full Pipeline)
// tag each row with `_pipelineKey`; single-pipeline boards carry theirs in the
// grid context. A status name both ladders share (Condition Clearance, Final
// Review, Inactive) groups rows from both pipelines on a mixed board — the
// first row decides.
const statusGroupPipeline = (node, context) =>
  node?.allLeafChildren?.[0]?.data?._pipelineKey ||
  context?.pipelineKey ||
  PIPELINE_KEYS.UNDERWRITING;

// Passed to AG Grid as `initialGroupOrderComparator`, so it only ever runs on
// GROUP rows — the ungrouped table is untouched, and a user sorting the group
// column still overrides it (AG Grid skips this callback once a sort is set).
const orderStatusRowGroups = ({ nodeA, nodeB, context }) => {
  // The blank group (no value — "Unassigned" on a role column) always sinks to
  // the bottom, just above the Total row, whatever the grouping.
  const aBlank = isBlankGroupKey(nodeA?.key);
  const bBlank = isBlankGroupKey(nodeB?.key);
  if (aBlank !== bBlank) return aBlank ? 1 : -1;
  // Only Loan Status gets a forced order; grouping by Originator, Loan Product,
  // etc. keeps AG Grid's default behavior.
  if (nodeA?.field !== "loanStatus" || nodeB?.field !== "loanStatus") return 0;
  const aPipeline = statusGroupPipeline(nodeA, context);
  const bPipeline = statusGroupPipeline(nodeB, context);
  // On a mixed board the pre-close ladder comes first, then acquisitions.
  if (aPipeline !== bPipeline) {
    return aPipeline === PIPELINE_KEYS.ACQUISITIONS ? 1 : -1;
  }
  const ladder =
    STATUS_GROUP_LADDERS[aPipeline] ||
    STATUS_GROUP_LADDERS[PIPELINE_KEYS.UNDERWRITING];
  const [aParent, aSub] = statusGroupRank(nodeA?.key, ladder);
  const [bParent, bSub] = statusGroupRank(nodeB?.key, ladder);
  if (aParent !== bParent) return aParent - bParent;
  if (aSub !== bSub) return aSub - bSub;
  return String(nodeA?.key ?? "").localeCompare(String(nodeB?.key ?? ""));
};

// ─── Group column labels ─────────────────────────────────────────────────────
const isBlankGroupKey = (key) =>
  key === null || key === undefined || String(key).trim() === "";

// How many loans the grand-total row covers: the root's leaf count after
// filters (the footer node's sibling is the root).
const grandTotalCount = (node, api) => {
  const n = node?.sibling?.allChildrenCount ?? node?.allChildrenCount;
  if (Number.isFinite(n)) return n;
  let count = 0;
  api?.forEachNodeAfterFilter?.((r) => {
    if (!r.group) count += 1;
  });
  return count;
};

// Grand-total row reads "Total (N)" instead of a bare "Total".
const pipelineGroupTotalValueGetter = ({ node, api }) =>
  node?.level === -1
    ? `Total (${grandTotalCount(node, api).toLocaleString()})`
    : `Total ${node?.key ?? ""}`;

// The group cell's label. The blank group on a role column (Processor,
// Underwriter, …) is the loans nobody holds that role on, so it reads
// "Unassigned" rather than an empty label next to its count.
const PipelineGroupInnerRenderer = (p) => {
  const node = p?.node;
  if (node?.footer) return pipelineGroupTotalValueGetter(p);
  if (node?.group && isBlankGroupKey(node.key)) {
    const isRoleColumn =
      node.rowGroupColumn?.getColDef?.()?.cellRenderer === CellRendererRoleUser;
    return isRoleColumn ? (
      <Box component="span" sx={{ fontStyle: "italic", color: grey[600] }}>
        Unassigned
      </Box>
    ) : null;
  }
  return p?.valueFormatted ?? p?.value ?? null;
};

// AG Grid keeps the grand-total row's group cell when the rows change (a
// status tab, the search box, a column filter), so "Total (N)" would keep the
// old count. Redraw just that row whenever the row model settles.
const redrawGrandTotalRow = ({ api }) => {
  const last = api?.getDisplayedRowCount?.() - 1;
  if (!(last >= 0)) return;
  const node = api.getDisplayedRowAtIndex(last);
  if (node?.footer && node.level === -1) api.redrawRows({ rowNodes: [node] });
};

const PIPELINE_AUTO_GROUP_COLUMN_DEF = {
  cellRendererParams: {
    innerRenderer: PipelineGroupInnerRenderer,
    totalValueGetter: pipelineGroupTotalValueGetter,
  },
};

export const SORT_OPTIONS = [
  { label: "Created Date (Newest)", field: "createDateTime", sort: "desc" },
  { label: "Created Date (Oldest)", field: "createDateTime", sort: "asc" },
  { label: "Recently Updated (Newest)", field: "updateDateTime", sort: "desc" },
  { label: "Recently Updated (Oldest)", field: "updateDateTime", sort: "asc" },
  {
    label: "Recently Added Comments (Newest)",
    field: "comments",
    sort: "desc",
  },
  { label: "Recently Added Comments (Oldest)", field: "comments", sort: "asc" },
];

const VIEW_OPTIONS = [
  { label: "All Loans", value: "all" },
  { label: "Only My Loans", value: "assigned" },
];

export const SORT_MENU_OPTIONS = SORT_OPTIONS.map((opt) => ({
  label: opt.label,
  value: `${opt.field}-${opt.sort}`,
}));

// ─── Safe Type Helpers ────────────────────────────────────────────────────────
// These guard against Python "None", "null", "undefined", wrong types, etc.

/** Returns true if a raw string value is a garbage/sentinel value */
const isGarbageValue = (val) =>
  val === null ||
  val === undefined ||
  val === "None" ||
  val === "null" ||
  val === "undefined" ||
  val === "";

/** Safely parse a JSON string that should be an array. Always returns []. */
const safeParseArray = (raw) => {
  if (isGarbageValue(raw)) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Safely read a string from localStorage. Returns null for garbage values. */
const safeReadString = (raw) => {
  if (isGarbageValue(raw)) return null;
  return typeof raw === "string" ? raw : null;
};

/** Safely read a boolean-as-string from localStorage. Returns false for garbage values. */
const safeReadBool = (raw) => {
  if (isGarbageValue(raw)) return false;
  return raw === "true";
};

// ─── Premade Layout Helpers ───────────────────────────────────────────────────

/** Sanitize a single column state value — converts Python "None" to null */
const sanitizeColumnStateValue = (val) => (val === "None" ? null : val);

/** Sanitize a premade layout object so all Python "None" strings become null */
const sanitizePremadeLayout = (layout) => ({
  ...layout,
  activeSort: layout.activeSort === "None" ? null : layout.activeSort,
  filters: {
    ...layout.filters,
    statusFilter:
      layout.filters?.statusFilter === "None"
        ? null
        : (layout.filters?.statusFilter ?? null),
    subStatusFilter:
      layout.filters?.subStatusFilter === "None"
        ? null
        : (layout.filters?.subStatusFilter ?? null),
    quickFilter:
      layout.filters?.quickFilter === "None"
        ? null
        : (layout.filters?.quickFilter ?? null),
    activeView: layout.filters?.activeView ?? "all",
  },
  columnState: layout.columnState?.map((col) => ({
    ...col,
    aggFunc: sanitizeColumnStateValue(col.aggFunc),
    sort: sanitizeColumnStateValue(col.sort),
    sortIndex: sanitizeColumnStateValue(col.sortIndex),
    pinned: sanitizeColumnStateValue(col.pinned),
    pivotIndex: sanitizeColumnStateValue(col.pivotIndex),
    rowGroupIndex: sanitizeColumnStateValue(col.rowGroupIndex),
  })),
});

// ─── Premade Layouts ──────────────────────────────────────────────────────────
// These are built-in templates that always appear in the layout dropdown.
// They work the same as user-created layouts but cannot be deleted, renamed,
// or overwritten. Users CAN set them as the default layout.

const PREMADE_LAYOUTS = [
  {
    id: "premade_sales",
    name: "Sales",
    isPremade: true,
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
    activeSort: "None",
    columnFilterModel: {
      loanStatus: {
        filterModels: [
          null,
          {
            filterType: "set",
            values: [
              "Appraisal Ordered",
              "Clear To Close",
              "Condition Clearance",
              "Final Review",
              "Inactive",
              "Processing",
              "Prospect",
              "Terms Accepted",
              "Underwriting",
            ],
          },
        ],
        filterType: "multi",
      },
    },
    columnState: [
      {
        aggFunc: "None",
        colId: "_id",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanNumber",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 90,
      },
      {
        aggFunc: "None",
        colId: "sync_loan_id",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 150,
      },
      {
        aggFunc: "None",
        colId: "assetId",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "createDateTime",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 115,
      },
      {
        aggFunc: "None",
        colId: "updateDateTime",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 115,
      },
      {
        aggFunc: "None",
        colId: "loanName",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 280,
      },
      {
        aggFunc: "None",
        colId: "comments",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 600,
      },
      {
        aggFunc: "None",
        colId: "loanStatus",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "currentStatusStartDate",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 210,
      },
      {
        aggFunc: "None",
        colId: "subStatus",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanCloser",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 120,
      },
      {
        aggFunc: "None",
        colId: "underwriter",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 120,
      },
      {
        aggFunc: "None",
        colId: "baseLoanToAsIsValue",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "baseLoanToPurchasePrice",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "borrowers",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "processor",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 120,
      },
      {
        aggFunc: "None",
        colId: "loanProductType",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanPurpose",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingScheduled",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingScheduledDate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingMonth",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingQuarter",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "closingWeek",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalArv",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalAsIsValue",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalAssignmentFees",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalConstructionBudget",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalCostBasis",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "totalInterestReserves",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalHoldback",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "baseLoanAmount",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalLoanAmount",
        flex: 1,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "totalLoanToArv",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalPurchasePrice",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "creditAuthorizations",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "discountPoints",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "dutchInterest",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "existingLoanInDefault",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "fundedDate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "interestRate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "isBalanceSheetClosing",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "expectedClosingDate",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      // Lendmarq divergence (see CLAUDE.md #1): the LQ role set is Loan Officer
      // (salesperson) / Sales Analyst / Processor / Underwriter / Closer — it has
      // no Account Manager or Structurer. Eastview's accountManager column state
      // becomes salesperson here, and the structurer entry is dropped.
      {
        aggFunc: "None",
        colId: "salesperson",
        flex: 0,
        hide: false,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 120,
      },
      {
        aggFunc: "None",
        colId: "salesAnalyst",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 120,
      },
      {
        aggFunc: "None",
        colId: "isTableFunding",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "lastViewDateTime",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "loanDocuments",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "maturityDate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "milestoneStatusChanges",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "netFunding",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "originationDate",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "originationPoints",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "permittedUsers",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "pipeline",
        flex: 0,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "referralSource",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "subjectProperties",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "openConditionsCount",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "None",
        colId: "openExceptionsCount",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
      {
        aggFunc: "sum",
        colId: "totalArv_1",
        flex: 1,
        hide: true,
        pinned: "None",
        pivot: false,
        pivotIndex: "None",
        rowGroup: false,
        rowGroupIndex: "None",
        sort: "None",
        sortIndex: "None",
        width: 200,
      },
    ],
    filters: {
      activeView: "all",
      quickFilter: "None",
      statusFilter: "None",
      subStatusFilter: "None",
    },
  },
  // ── Add more premade layouts here as needed ──
  // {
  //   id: "premade_underwriting",
  //   name: "Underwriting",
  //   isPremade: true,
  //   ...
  // },
];

/** Sanitized premade layouts ready for use (all Python "None" → null) */
const SANITIZED_PREMADE_LAYOUTS = PREMADE_LAYOUTS.map(sanitizePremadeLayout);

/** Quick lookup set for premade layout IDs */
const PREMADE_LAYOUT_IDS = new Set(SANITIZED_PREMADE_LAYOUTS.map((l) => l.id));

/** Check if a layout ID belongs to a premade layout */
const isPremadeLayoutId = (id) => PREMADE_LAYOUT_IDS.has(id);

// ─── Storage Helpers ──────────────────────────────────────────────────────────

const LAYOUTS_STORAGE_KEY = nsKey("lo-pipeline-layouts");
const DEFAULT_LAYOUT_STORAGE_KEY = nsKey("lo-pipeline-default-layout");
const ASSIGNED_TO_ME_STORAGE_KEY = nsKey("lo-pipeline-assigned-to-me");
const COMPACT_STORAGE_KEY = nsKey("lo-pipeline-compact");
const COMPACT_HINT_DISMISSED_KEY = nsKey("lo-pipeline-compact-hint-dismissed");
// "Remember where I left off": a boolean toggle plus the captured snapshot of
// the user's last view (columns, sort, filters). When the toggle is on, the
// snapshot is restored on the next visit — taking precedence over the default
// layout. The snapshot stays device-local; only the toggle syncs to the profile.
const REMEMBER_SESSION_STORAGE_KEY = nsKey("lo-pipeline-remember-session");
const LAST_SESSION_STORAGE_KEY = nsKey("lo-pipeline-last-session");

// Comments column width: full preview vs. the condensed compact view.
const DEFAULT_COMMENTS_WIDTH = 600;
const COMPACT_COMMENTS_WIDTH = 220;

const getSavedLayouts = () => {
  try {
    return safeParseArray(localStorage.getItem(LAYOUTS_STORAGE_KEY));
  } catch {
    return [];
  }
};
const persistLayouts = (l) =>
  localStorage.setItem(
    LAYOUTS_STORAGE_KEY,
    JSON.stringify(Array.isArray(l) ? l : []),
  );

const getDefaultLayoutId = () =>
  safeReadString(localStorage.getItem(DEFAULT_LAYOUT_STORAGE_KEY));

const setDefaultLayoutId = (id) =>
  localStorage.setItem(DEFAULT_LAYOUT_STORAGE_KEY, id ?? "");

const getPersistedAssignedToMe = () => {
  try {
    return safeReadBool(localStorage.getItem(ASSIGNED_TO_ME_STORAGE_KEY));
  } catch {
    return false;
  }
};
const persistAssignedToMe = (v) =>
  localStorage.setItem(ASSIGNED_TO_ME_STORAGE_KEY, v ? "true" : "false");

const getPersistedCompact = () => {
  try {
    // Default to compact ON when the user hasn't set a preference yet; an
    // explicit "false" stored by toggling it off is still respected.
    const stored = localStorage.getItem(COMPACT_STORAGE_KEY);
    if (stored === null) return true;
    return safeReadBool(stored);
  } catch {
    return true;
  }
};
const persistCompact = (v) =>
  localStorage.setItem(COMPACT_STORAGE_KEY, v ? "true" : "false");

const getPersistedCompactHintDismissed = () => {
  try {
    return safeReadBool(localStorage.getItem(COMPACT_HINT_DISMISSED_KEY));
  } catch {
    return false;
  }
};
const persistCompactHintDismissed = () =>
  localStorage.setItem(COMPACT_HINT_DISMISSED_KEY, "true");

const getPersistedRememberSession = () => {
  try {
    // Default ON when the user hasn't set a preference yet; an explicit
    // "false" stored by toggling it off is still respected.
    const stored = localStorage.getItem(REMEMBER_SESSION_STORAGE_KEY);
    if (stored === null) return true;
    return safeReadBool(stored);
  } catch {
    return true;
  }
};
const persistRememberSession = (v) =>
  localStorage.setItem(REMEMBER_SESSION_STORAGE_KEY, v ? "true" : "false");

const getPersistedLastSession = () => {
  try {
    const raw = localStorage.getItem(LAST_SESSION_STORAGE_KEY);
    if (isGarbageValue(raw)) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};
const persistLastSession = (state) => {
  try {
    localStorage.setItem(LAST_SESSION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / serialization failures — it's a best-effort convenience
  }
};
const clearLastSession = () => {
  try {
    localStorage.removeItem(LAST_SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
};

// ─── Filter Helpers ───────────────────────────────────────────────────────────

const SUB_FILTER_NONE = "__none__";

const loanBelongsToStatusGroup = (loan, parentStatus) => {
  if (loan?.loanStatus === parentStatus) return true;
  const subs = LOAN_SUBSTATUS_MAP[parentStatus];
  return subs && subs.includes(loan?.loanStatus);
};

const getLoansForStatusGroup = (loans, parentStatus) =>
  loans?.filter((l) => loanBelongsToStatusGroup(l, parentStatus)) || [];

const isSubStatusField = (subStatusValue) =>
  SUBSTATUS_TO_PARENT_MAP[subStatusValue] === "Processing";

const filterLoansBySubStatus = (loans, parentStatus, subStatusFilter) => {
  const groupLoans = getLoansForStatusGroup(loans, parentStatus);
  if (!subStatusFilter) return groupLoans;
  if (subStatusFilter === SUB_FILTER_NONE) {
    return groupLoans.filter(
      (l) =>
        l?.loanStatus === parentStatus &&
        (isNil(l?.subStatus) || l?.subStatus === ""),
    );
  }
  if (isSubStatusField(subStatusFilter)) {
    return groupLoans.filter(
      (l) => l?.loanStatus === parentStatus && l?.subStatus === subStatusFilter,
    );
  }
  return groupLoans.filter(
    (l) =>
      (l?.loanStatus === parentStatus && l?.subStatus === subStatusFilter) ||
      l?.loanStatus === subStatusFilter,
  );
};

const countLoansForSubStatus = (loans, parentStatus, subStatusValue) =>
  size(filterLoansBySubStatus(loans, parentStatus, subStatusValue));

const filterLoansByUserEmail = (loans = [], userEmail) => {
  if (!userEmail) return loans;
  return loans?.filter(
    (l) =>
      // Lendmarq divergence (see CLAUDE.md #1): no accountManager / structurer.
      l?.salesperson === userEmail ||
      l?.salesAnalyst === userEmail ||
      l?.loanCloser === userEmail ||
      l?.processor === userEmail ||
      l?.underwriter === userEmail ||
      l?.permittedUsers?.includes(userEmail),
  );
};

// ─── useLayouts Hook ──────────────────────────────────────────────────────────

const useLayouts = ({ gridRef, activeSort, filters, syncPreferences }) => {
  const [userLayouts, setUserLayouts] = useState(() => getSavedLayouts());
  const [defaultId, setDefaultId] = useState(() => getDefaultLayoutId());

  // Merge premade layouts with user-created layouts
  const layouts = useMemo(
    () => [
      ...SANITIZED_PREMADE_LAYOUTS,
      ...(Array.isArray(userLayouts) ? userLayouts : []),
    ],
    [userLayouts],
  );

  useEffect(() => {
    const onHydrated = () => {
      setUserLayouts(getSavedLayouts());
      setDefaultId(getDefaultLayoutId());
    };
    window.addEventListener("preferences-hydrated", onHydrated);
    return () => window.removeEventListener("preferences-hydrated", onHydrated);
  }, []);

  const saveUserLayouts = useCallback(
    (updated) => {
      // Only persist user layouts — never persist premade layouts to localStorage
      const userOnly = (Array.isArray(updated) ? updated : []).filter(
        (l) => !l.isPremade,
      );
      setUserLayouts(userOnly);
      persistLayouts(userOnly);
      syncPreferences?.();
    },
    [syncPreferences],
  );

  const captureCurrentState = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return null;
    return {
      columnState: api.getColumnState(),
      columnFilterModel: api.getFilterModel(),
      activeSort: activeSort || null,
      filters: {
        statusFilter: filters?.statusFilter ?? null,
        subStatusFilter: filters?.subStatusFilter ?? null,
        activeView: filters?.activeView ?? "all",
        quickFilter: filters?.quickFilter ?? null,
      },
    };
  }, [gridRef, activeSort, filters]);

  const createLayout = useCallback(
    (name) => {
      const state = captureCurrentState();
      if (!state) return;
      const newLayout = {
        id: `layout_${Date.now()}`,
        name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...state,
      };
      const updated = [
        ...(Array.isArray(userLayouts) ? userLayouts : []),
        newLayout,
      ];
      saveUserLayouts(updated);
      return newLayout;
    },
    [userLayouts, captureCurrentState, saveUserLayouts],
  );

  const updateLayout = useCallback(
    (layoutId) => {
      // Premade layouts cannot be overwritten
      if (isPremadeLayoutId(layoutId)) return;
      const state = captureCurrentState();
      if (!state) return;
      const safe = Array.isArray(userLayouts) ? userLayouts : [];
      const updated = safe.map((l) =>
        l.id === layoutId
          ? { ...l, ...state, updatedAt: new Date().toISOString() }
          : l,
      );
      saveUserLayouts(updated);
    },
    [userLayouts, captureCurrentState, saveUserLayouts],
  );

  const renameLayout = useCallback(
    (layoutId, newName) => {
      // Premade layouts cannot be renamed
      if (isPremadeLayoutId(layoutId)) return;
      const safe = Array.isArray(userLayouts) ? userLayouts : [];
      saveUserLayouts(
        safe.map((l) => (l.id === layoutId ? { ...l, name: newName } : l)),
      );
    },
    [userLayouts, saveUserLayouts],
  );

  const deleteLayout = useCallback(
    (layoutId) => {
      // Premade layouts cannot be deleted
      if (isPremadeLayoutId(layoutId)) return;
      const safe = Array.isArray(userLayouts) ? userLayouts : [];
      saveUserLayouts(safe.filter((l) => l.id !== layoutId));
      if (defaultId === layoutId) {
        setDefaultId(null);
        setDefaultLayoutId(null);
        syncPreferences?.();
      }
    },
    [userLayouts, defaultId, saveUserLayouts, syncPreferences],
  );

  const setAsDefault = useCallback(
    (layoutId) => {
      const next = layoutId ?? null;
      setDefaultId(next);
      setDefaultLayoutId(next);
      syncPreferences?.();
    },
    [syncPreferences],
  );

  const toggleDefault = useCallback(
    (layoutId) => {
      const isRemoving = defaultId === layoutId;
      const next = isRemoving ? null : layoutId;
      // Write to localStorage FIRST (synchronous, immediate)
      setDefaultLayoutId(next);
      // Then update React state
      setDefaultId(next);
      // Defer sync so it picks up the new localStorage value
      setTimeout(() => syncPreferences?.(), 50);
    },
    [defaultId, syncPreferences],
  );

  const getDefaultLayout = useCallback(() => {
    if (!defaultId) return null;
    // Search in merged layouts (premade + user)
    return layouts.find((l) => l.id === defaultId) || null;
  }, [layouts, defaultId]);

  return {
    layouts,
    defaultId,
    createLayout,
    updateLayout,
    renameLayout,
    deleteLayout,
    setAsDefault,
    toggleDefault,
    getDefaultLayout,
    // Exposed so the screen can snapshot the live view for the
    // "Remember where I left off" feature (same shape as a saved layout).
    captureCurrentState,
  };
};

// ─── JSON Cell Renderer ───────────────────────────────────────────────────────

// Renders a small "JSON" button in the cell; clicking it opens a modal with a
// searchable/collapsible preview of the full row data. Each cell owns its own
// modal state. Hidden by default (the column is `hide: true`).
const CellRendererJsonButton = (params) => {
  const [open, setOpen] = useState(false);
  if (params?.node?.group) return null;
  const data = params?.data;
  if (!data) return null;
  return (
    <>
      <Button
        size="small"
        variant="text"
        startIcon={<DataObjectOutlined sx={{ fontSize: 15 }} />}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{ fontSize: 11, minWidth: 0 }}
      >
        JSON
      </Button>
      <BasicModal
        show={open}
        onClose={() => setOpen(false)}
        title={`Loan ${data?.loanNumber ?? ""} — JSON`}
        icon={<DataObjectOutlined className="thin" />}
        maxWidth="md"
        bodySx={{ p: 2 }}
      >
        <JsonPreview values={data} defaultCollapse={2} />
      </BasicModal>
    </>
  );
};

// ─── Pipeline Nav Item (top-level clickable header) ──────────────────────────

// ─── Main Component ───────────────────────────────────────────────────────────
// ─── Main Component ───────────────────────────────────────────────────────────
// ─── Main Component ───────────────────────────────────────────────────────────

const LoanOfficerPipelineScreen = ({ children }) => {
  // The pipeline grid resolves assigned-user emails to names via the shared
  // `users` slice — pull it lazily (cache-gated) instead of relying on a global
  // eager fetch.
  useEnsureUsers();
  const { syncPreferences } = useUserPreferences();
  const isFetchingRef = useRef(false);
  const gridRef = useRef(null);
  const gridReadyRef = useRef(false);
  const defaultAppliedRef = useRef(false);

  const { loading, getUserPipeline } = usePipelineHook();
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const { userEmail } = useReduxHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const { name: pipelineName } = useParams();

  const [quickFilter, setQuickFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [subStatusFilter, setSubStatusFilter] = useState(null);
  const [activeSort, setActiveSort] = useState(null);
  const [activeLayoutId, setActiveLayoutId] = useState(null);

  const [activeView, setActiveView] = useState(() => {
    if (getPersistedAssignedToMe()) return "assigned";
    return "all";
  });

  const [assignedToMe, setAssignedToMe] = useState(() =>
    getPersistedAssignedToMe(),
  );

  // "Remember where I left off" — when on, the live view (columns, sort,
  // filters) is snapshotted on every change and restored on the next visit.
  const [rememberSession, setRememberSession] = useState(() =>
    getPersistedRememberSession(),
  );
  // Suppresses session snapshots while we're programmatically restoring a
  // layout/session on grid-ready, so the restore doesn't immediately overwrite
  // the saved snapshot with a half-applied (default) state.
  const restoringSessionRef = useRef(false);

  const [isMinimized, setIsMinimized] = useState(false);
  // Compact mode collapses multi-line rows (no chips/badges, smaller comments
  // column). Persisted to localStorage so it's remembered per browser.
  const [isCompact, setIsCompact] = useState(() => getPersistedCompact());
  // First-visit "New: Compact mode" hint, dismissable + remembered.
  const [compactBtnEl, setCompactBtnEl] = useState(null);
  const [showCompactHint, setShowCompactHint] = useState(
    () => !getPersistedCompactHintDismissed(),
  );

  const dismissCompactHint = useCallback(() => {
    setShowCompactHint(false);
    persistCompactHintDismissed();
  }, []);

  // Temporary close (X) — hides for this session only; will show again next
  // visit, unlike "Don't show again" which persists the dismissal.
  const closeCompactHint = useCallback(() => setShowCompactHint(false), []);

  const handleCompactChange = useCallback(
    (checked) => {
      setIsCompact(checked);
      persistCompact(checked);
      syncPreferences();
      // Hide the hint for this session only (don't persist) — only the explicit
      // "Don't show again" button should permanently dismiss it.
      closeCompactHint();
    },
    [syncPreferences, closeCompactHint],
  );

  const handleViewChange = useCallback(
    (val) => {
      const next = val || "all";
      setActiveView(next);
      const isAssigned = next === "assigned";
      setAssignedToMe(isAssigned);
      persistAssignedToMe(isAssigned);
      syncPreferences();
    },
    [syncPreferences],
  );

  useEffect(() => {
    const onHydrated = () => {
      setAssignedToMe(getPersistedAssignedToMe());
      setRememberSession(getPersistedRememberSession());
    };
    window.addEventListener("preferences-hydrated", onHydrated);
    return () => window.removeEventListener("preferences-hydrated", onHydrated);
  }, []);

  const layoutAnchorRef = useRef(null);
  const layoutMenuRef = useRef(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState("");
  const [saveDialogMode, setSaveDialogMode] = useState("create");
  const [saveDialogTargetId, setSaveDialogTargetId] = useState(null);

  // ── Memoized filters object for useLayouts ──
  const currentFilters = useMemo(
    () => ({
      statusFilter,
      subStatusFilter,
      activeView,
      quickFilter,
    }),
    [statusFilter, subStatusFilter, activeView, quickFilter],
  );

  const {
    layouts,
    defaultId,
    createLayout,
    updateLayout,
    renameLayout,
    deleteLayout,
    setAsDefault,
    toggleDefault,
    getDefaultLayout,
    captureCurrentState,
  } = useLayouts({
    gridRef,
    activeSort,
    filters: currentFilters,
    syncPreferences,
  });

  const applyLayout = useCallback(
    (layout, { skipAutoDefault = false } = {}) => {
      const api = gridRef.current?.api;
      if (!api || !layout) return;

      // Saved layouts can come from the shared user profile (the parent
      // rtl-portal-frontend has more/different pipeline columns + an older
      // filter type). Applying a columnState / filterModel that references
      // columns this trimmed grid doesn't have hangs AG Grid hard. Sanitize
      // every saved blob against the grid's CURRENT columns first.
      const existingColIds = new Set(
        (api.getColumnState() || []).map((c) => c.colId),
      );

      try {
        // Restore column state — sanitize ("None" strings → null, validate flex/
        // pinned/sort) and keep only columns that still exist here.
        if (Array.isArray(layout.columnState)) {
          const safeState = sanitizeSavedColumnState(layout.columnState).filter(
            (cs) => existingColIds.has(cs.colId),
          );
          if (safeState.length)
            api.applyColumnState({ state: safeState, applyOrder: true });
        }

        // Restore AG Grid column filter model — only keys for existing columns.
        if (
          layout.columnFilterModel &&
          typeof layout.columnFilterModel === "object"
        ) {
          const safeModel = Object.fromEntries(
            Object.entries(layout.columnFilterModel).filter(([colId]) =>
              existingColIds.has(colId),
            ),
          );
          api.setFilterModel(Object.keys(safeModel).length ? safeModel : null);
        } else {
          api.setFilterModel(null);
        }

        // Restore sort — only if the sorted column still exists.
        if (layout.activeSort && existingColIds.has(layout.activeSort.field)) {
          setActiveSort(layout.activeSort);
          api.applyColumnState({
            state: [
              { colId: layout.activeSort.field, sort: layout.activeSort.sort },
            ],
            defaultState: { sort: null },
          });
        } else {
          setActiveSort(null);
          // The applied columnState already carries the full sort state —
          // including header-click sorts that never set `activeSort` (only the
          // Sort dropdown does). Only force-clear when there's no columnState to
          // restore it from; otherwise this wipes the sort just restored above.
          if (
            !Array.isArray(layout.columnState) ||
            !layout.columnState.length
          ) {
            api.applyColumnState({ defaultState: { sort: null } });
          }
        }
      } catch (err) {
        // A malformed/stale saved layout shouldn't take the page down.
        console.error(
          "[applyLayout] failed to apply saved layout cleanly:",
          err,
        );
      }

      // Restore applied filters (statusFilter, subStatusFilter, activeView, quickFilter)
      if (layout.filters) {
        setStatusFilter(layout.filters.statusFilter ?? null);
        setSubStatusFilter(layout.filters.subStatusFilter ?? null);
        setQuickFilter(layout.filters.quickFilter ?? null);

        // Restore view (all / assigned) and sync dependent state
        const savedView = layout.filters.activeView ?? "all";
        setActiveView(savedView);
        const isAssigned = savedView === "assigned";
        setAssignedToMe(isAssigned);
        persistAssignedToMe(isAssigned);
      }

      setActiveLayoutId(layout.id);

      // Auto-set as default unless explicitly skipped
      if (!skipAutoDefault) {
        setAsDefault(layout.id);
      }
    },
    [setAsDefault],
  );
  // Snapshot the live view into the last-session slot. No-op unless the toggle
  // is on, the grid is ready, and we're not mid-restore. Cheap enough to call
  // on every grid state change.
  const persistSessionNow = useCallback(() => {
    if (!rememberSession) return;
    if (!gridReadyRef.current) return;
    if (restoringSessionRef.current) return;
    const state = captureCurrentState();
    if (state) persistLastSession(state);
  }, [rememberSession, captureCurrentState]);

  const handleToggleRememberSession = useCallback(() => {
    setRememberSession((prev) => {
      const next = !prev;
      persistRememberSession(next);
      if (next) {
        // Capture the current view immediately so there's something to restore
        // even if the user navigates away without touching the table again.
        const state = captureCurrentState();
        if (state) persistLastSession(state);
      } else {
        clearLastSession();
      }
      syncPreferences?.();
      return next;
    });
  }, [captureCurrentState, syncPreferences]);

  // Re-persist whenever the app-level filters change (these live in React state
  // and don't fire AG Grid's onStateUpdated). Grid-driven changes (columns,
  // sort, header filters) are captured via the table's onStateUpdated below.
  useEffect(() => {
    persistSessionNow();
  }, [
    statusFilter,
    subStatusFilter,
    activeView,
    quickFilter,
    activeSort,
    persistSessionNow,
  ]);

  const handleGridReady = useCallback(() => {
    gridReadyRef.current = true;
    if (defaultAppliedRef.current) return;
    defaultAppliedRef.current = true;
    // Guard snapshots through the whole restore (including the table's deferred
    // compact-width applyColumnState) so we don't clobber the saved snapshot.
    restoringSessionRef.current = true;
    const finishRestore = () =>
      setTimeout(() => {
        restoringSessionRef.current = false;
      }, 250);

    // "Where you left off" wins over the default layout when enabled + present.
    let toApply = null;
    if (getPersistedRememberSession()) {
      const last = getPersistedLastSession();
      if (last) toApply = last;
    }
    if (!toApply) {
      // Read directly from localStorage to avoid stale React state
      const persistedDefaultId = getDefaultLayoutId();
      // Look up the default layout from both persisted user layouts AND premade layouts
      const persistedLayouts = getSavedLayouts();
      const allAvailableLayouts = [
        ...SANITIZED_PREMADE_LAYOUTS,
        ...persistedLayouts,
      ];
      toApply = persistedDefaultId
        ? allAvailableLayouts.find((l) => l.id === persistedDefaultId) || null
        : null;
    }

    if (toApply) {
      setTimeout(() => {
        applyLayout(toApply, { skipAutoDefault: true });
        finishRestore();
      }, 100);
    } else {
      finishRestore();
    }
  }, [applyLayout]);
  // Pipeline filtering
  const filteredPipelineLoans = useMemo(() => {
    const base = loanPipeline || [];
    if (pipelineName === "all") return base;
    if (FALLBACK_ALL_PIPELINE_NAMES.includes(pipelineName)) return base;
    if (pipelineName === "underwriting")
      return base.filter((l) => l?.pipeline === "Underwriting");
    if (pipelineName === "borrower-portal-scenarios")
      return base.filter((l) => l?.pipeline === "Borrower Portal Scenarios");
    if (pipelineName === "scenarios")
      return base.filter((l) => l?.pipeline === "Scenario");
    return [];
  }, [loanPipeline, pipelineName]);
  const filteredPipelineLoansByAssignment = useMemo(() => {
    if (!assignedToMe) return filteredPipelineLoans;
    return filterLoansByUserEmail(filteredPipelineLoans, userEmail);
  }, [filteredPipelineLoans, assignedToMe, userEmail]);

  const filteredPipelineLoansByStatus = useMemo(() => {
    if (!statusFilter) return filteredPipelineLoansByAssignment;
    const hasSubStatuses = !!LOAN_SUBSTATUS_MAP[statusFilter];
    if (hasSubStatuses)
      return filterLoansBySubStatus(
        filteredPipelineLoansByAssignment,
        statusFilter,
        subStatusFilter,
      );
    return filteredPipelineLoansByAssignment?.filter(
      (l) => l?.loanStatus === statusFilter,
    );
  }, [filteredPipelineLoansByAssignment, statusFilter, subStatusFilter]);

  const getStatusGroupCount = useCallback(
    (parentStatus) => {
      if (!parentStatus) return size(filteredPipelineLoansByAssignment);
      return size(
        getLoansForStatusGroup(filteredPipelineLoansByAssignment, parentStatus),
      );
    },
    [filteredPipelineLoansByAssignment],
  );

  const viewMenuOptions = useMemo(() => {
    const allCount = size(filteredPipelineLoans);
    const assignedCount = size(
      filterLoansByUserEmail(filteredPipelineLoans, userEmail),
    );

    const counts = {
      all: allCount,
      assigned: assignedCount,
    };

    return VIEW_OPTIONS.map((opt) => ({
      ...opt,
      count: counts[opt.value] ?? null,
    }));
  }, [filteredPipelineLoans, userEmail]);

  useEffect(() => {
    setSubStatusFilter(null);
  }, [statusFilter]);

  const activeSortValue = useMemo(
    () => (activeSort ? `${activeSort.field}-${activeSort.sort}` : null),
    [activeSort],
  );

  const handleSortChange = useCallback((val) => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (!val) {
      api.applyColumnState({ defaultState: { sort: null } });
      setActiveSort(null);
      return;
    }
    const sortOption = SORT_OPTIONS.find((o) => `${o.field}-${o.sort}` === val);
    if (!sortOption) return;
    api.applyColumnState({
      state: [{ colId: sortOption.field, sort: sortOption.sort }],
      defaultState: { sort: null },
    });
    setActiveSort(sortOption);
  }, []);

  const handleSaveNewLayout = useCallback(() => {
    setSaveDialogMode("create");
    setSaveDialogName("");
    setSaveDialogTargetId(null);
    setSaveDialogOpen(true);
    layoutMenuRef.current?.close();
  }, []);

  const handleRenameLayout = useCallback((layout) => {
    setSaveDialogMode("rename");
    setSaveDialogName(layout.name);
    setSaveDialogTargetId(layout.id);
    setSaveDialogOpen(true);
    layoutMenuRef.current?.close();
  }, []);

  const handleSaveDialogConfirm = useCallback(() => {
    const trimmed = saveDialogName.trim();
    if (!trimmed) return;
    if (saveDialogMode === "create") {
      const nl = createLayout(trimmed);
      if (nl) {
        setActiveLayoutId(nl.id);
        // Auto-set newly created layout as default
        setAsDefault(nl.id);
      }
    } else if (saveDialogMode === "rename" && saveDialogTargetId)
      renameLayout(saveDialogTargetId, trimmed);
    setSaveDialogOpen(false);
    setSaveDialogName("");
  }, [
    saveDialogName,
    saveDialogMode,
    saveDialogTargetId,
    createLayout,
    renameLayout,
    setAsDefault,
  ]);

  const handleUpdateLayout = useCallback(
    (layoutId) => {
      updateLayout(layoutId);
      layoutMenuRef.current?.close();
    },
    [updateLayout],
  );

  const handleDeleteLayout = useCallback(
    (layoutId) => {
      deleteLayout(layoutId);
      if (activeLayoutId === layoutId) {
        const defaultLayout = getDefaultLayout();
        if (defaultLayout && defaultLayout.id !== layoutId) {
          applyLayout(defaultLayout, { skipAutoDefault: true });
        } else {
          const api = gridRef.current?.api;
          if (api) {
            api.resetColumnState();
            api.applyColumnState({ defaultState: { sort: null } });
            api.setFilterModel(null);
          }
          setActiveSort(null);
          setActiveLayoutId(null);
          setStatusFilter(null);
          setSubStatusFilter(null);
          setQuickFilter(null);
          setActiveView("all");
          setAssignedToMe(false);
          persistAssignedToMe(false);
        }
      }
    },
    [deleteLayout, activeLayoutId, getDefaultLayout, applyLayout],
  );

  const handleApplyLayout = useCallback(
    (layout) => {
      applyLayout(layout);
      layoutMenuRef.current?.close();
    },
    [applyLayout],
  );
  const handleClearActiveLayout = useCallback(() => {
    const api = gridRef.current?.api;
    if (api) {
      api.resetColumnState();
      api.applyColumnState({ defaultState: { sort: null } });
      api.setFilterModel(null);
    }
    setActiveSort(null);
    setActiveLayoutId(null);
    setStatusFilter(null);
    setSubStatusFilter(null);
    setQuickFilter(null);
    setActiveView("all");
    setAssignedToMe(false);
    persistAssignedToMe(false);

    // Clear the default layout so nothing auto-applies on next load
    setAsDefault(null);
    layoutMenuRef.current?.close();
  }, [setAsDefault]);

  const fetchLoanPipeline = useCallback(() => {
    // TEMP: serve the hardcoded loan as a one-row pipeline instead of fetching,
    // until the pipeline endpoint is ready. Restore the block below to re-enable.
    dispatch(loanPipelineSet([mockLoanDrilldown]));
    return;

    /* eslint-disable no-unreachable */
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    getUserPipeline({
      onSuccessFn: (data) => {
        dispatch(loanPipelineSet(data));
        isFetchingRef.current = false;
      },
      onErrorFn: (err) => {
        console.error("GetUserPipeline error:", err);
        isFetchingRef.current = false;
      },
    });
    /* eslint-enable no-unreachable */
  }, [getUserPipeline, dispatch]);

  const onCellClicked = useCallback(
    (params) => {
      if (params?.colDef?.field === "loanNumber") return;
      const el = params.event?.target;
      if (
        (el instanceof Element && el.closest("button")) ||
        (el instanceof Element && el.closest("a"))
      )
        return;
      if (isNil(params?.data?.loanNumber)) {
        console.warn("No loanNumber found");
        return;
      }
      if (params?.colDef?.field === "loanName")
        navigate(`/loan/${params.data._id}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (isNil(loanPipeline) && !isFetchingRef.current) fetchLoanPipeline();
    if (!isNil(loanDrilldown)) clearLoanDrilldownData();
  }, [loanPipeline, fetchLoanPipeline, loanDrilldown]);

  useEffect(() => {
    if (!pipelineName || !PIPELINE_NAME_TO_TITLE_MAP[pipelineName])
      navigate("/loans/pre-close", { replace: true });
  }, [pipelineName, navigate]);

  const activeLayoutName = useMemo(() => {
    if (!activeLayoutId) return null;
    const safe = Array.isArray(layouts) ? layouts : [];
    return safe.find((l) => l.id === activeLayoutId)?.name || null;
  }, [activeLayoutId, layouts]);

  // Split layouts into premade vs user for rendering with a separator
  const premadeLayouts = useMemo(
    () => layouts.filter((l) => l.isPremade),
    [layouts],
  );
  const userCreatedLayouts = useMemo(
    () => layouts.filter((l) => !l.isPremade),
    [layouts],
  );

  return (
    <Box
      sx={{
        background: "#ffffff",
        display: "flex",
        overflow: "hidden",
        height: "100%",
      }}
    >
      {/* ── Main Content ── */}
      <ScreenContent
        sx={{
          height: "100%",
          p: 0,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            background: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              p: 1,
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
              flexGrow: isMinimized ? 0 : 1,
              width: "100%",
            }}
          >
            {isMinimized ? (
              <Flx
                g={2}
                ac
                sx={{
                  flexGrow: 0,
                  border: "1px solid #e0e0e0",
                  background: grey[50],
                  p: 1,
                  borderRadius: "8px",
                }}
              >
                <Flx ac>
                  <Flx ac>
                    <ForkRightOutlined
                      className="thin"
                      sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}
                    />

                    <Txt secondary ml={0.5} sx={{ fontSize: 12 }}>
                      Pipeline:{" "}
                      <b
                        style={{
                          textTransform: "capitalize",
                          color: level1.text,
                        }}
                      >
                        {pipelineName || "All"}
                      </b>
                    </Txt>
                  </Flx>
                </Flx>
                <EastOutlined className="thin" sx={{ fontSize: "15px" }} />

                <Txt secondary sx={{ fontSize: 12 }}>
                  Loan Status:{" "}
                  <b
                    style={{
                      textTransform: "capitalize",
                      color: level1.text,
                    }}
                  >
                    {statusFilter || "All"}
                  </b>
                </Txt>
                {!isNil(subStatusFilter) && subStatusFilter !== "__none__" && (
                  <>
                    <EastOutlined className="thin" sx={{ fontSize: "15px" }} />

                    <Txt secondary sx={{ fontSize: 12 }}>
                      Sub Status:{" "}
                      <b
                        style={{
                          textTransform: "capitalize",
                          color: level1.text,
                        }}
                      >
                        {isNil(subStatusFilter) ||
                          subStatusFilter === "__none__" ||
                          "All"}
                      </b>
                    </Txt>
                  </>
                )}
                <Tooltip onClick={() => setIsMinimized(false)} title="Expand">
                  <IconButton sx={{ ml: 2.5 }} size="small">
                    <ExpandMoreOutlined />
                  </IconButton>
                </Tooltip>
              </Flx>
            ) : (
              <Flx
                g={1}
                ac
                sx={{
                  // alignItems: "flex-start",
                  flexGrow: 1,
                  // p: 1,
                  // px: 1.2,
                  // border: "1px solid #e0e0e0",
                  // background: grey[50],
                  // borderRadius: "8px",
                }}
              >
                <PipelineStatusFilterSwitcher
                  loans={filteredPipelineLoansByAssignment}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  getStatusGroupCount={getStatusGroupCount}
                  sx={{
                    borderBottomLeftRadius: isNil(
                      LOAN_SUBSTATUS_MAP?.[statusFilter],
                    )
                      ? "8px"
                      : 0,
                  }}
                  subStatusContent={
                    <SubStatusSwitcher
                      loans={filteredPipelineLoansByAssignment}
                      statusFilter={statusFilter}
                      subStatusFilter={subStatusFilter}
                      setSubStatusFilter={setSubStatusFilter}
                      sx={{
                        background: "#eceff196",
                        border: "1px solid #e0e0e0",
                        borderTop: "none",
                        borderRadius: "8px",
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0,
                        px: 1,
                        width: "max-content",
                      }}
                    />
                  }
                />
              </Flx>
            )}
          </Box>

          <Flx
            ac
            jb
            wrap
            g={2}
            sx={{
              p: 1,
              pt: 1,
              borderTop: "1px solid #e0e0e0",
              flexGrow: 1,
              px: 1.2,
            }}
          >
            <Flx ac g={1} wrap>
              <SearchInput
                sx={{
                  width: "225px",
                  ".MuiInputBase-root": {
                    borderRadius: "50px !important",
                    background: "#ffffff",
                    fontSize: "11px",
                    pl: 1.2,
                    pr: 1,
                    ".MuiSvgIcon-root": { fontSize: "11px" },
                  },
                  input: { height: "unset", fontSize: "11px" },
                  fieldset: { border: "none" },
                }}
                value={quickFilter}
                onChange={setQuickFilter}
              />
              <MyLoansCheckbox
                activeView={activeView}
                onViewChange={handleViewChange}
                assignedCount={
                  viewMenuOptions.find((o) => o.value === "assigned")?.count ??
                  0
                }
              />
            </Flx>
            <Flx ac g={1.4}>
              <Badge
                color="error"
                variant="dot"
                invisible={!showCompactHint}
                overlap="rectangular"
              >
                <Tooltip title="Condense rows — hides chips, badges, and shrinks the comments column">
                  <Button
                    ref={setCompactBtnEl}
                    onClick={() => handleCompactChange(!isCompact)}
                    variant="outlined"
                    size="medium"
                    color={isCompact ? "primary" : "gray"}
                    startIcon={
                      <Checkbox
                        checked={isCompact}
                        size="small"
                        tabIndex={-1}
                        disableRipple
                        sx={{
                          p: 0,
                          ml: 0.5,
                          pointerEvents: "none",
                          ".MuiSvgIcon-root": { fontSize: 16 },
                        }}
                      />
                    }
                    sx={{
                      border: "1px solid #e0e0e0",
                      fontWeight: isCompact ? 600 : 400,
                      textTransform: "none",
                      px: 1.2,
                      borderRadius: "8px",
                    }}
                  >
                    Compact
                  </Button>
                </Tooltip>
              </Badge>
              <CompactModeHintPopper
                anchorEl={compactBtnEl}
                open={showCompactHint && Boolean(compactBtnEl)}
                onClose={closeCompactHint}
                onDismiss={dismissCompactHint}
              />
              <ButtonMenu
                menuTitle="Sort Options"
                options={SORT_MENU_OPTIONS}
                value={activeSortValue}
                startIcon={
                  <SwapVertOutlined
                    sx={{ fontSize: "16px !important", ml: 0.5 }}
                  />
                }
                onChange={handleSortChange}
                buttonText={activeSortValue ? null : "Sort: None"}
                allowDeselect
                showBadge={activeSortValue !== null}
                menuMinWidth={280}
              />
              <Badge color="primary" variant="dot" invisible={!activeLayoutId}>
                <Button
                  startIcon={
                    <ViewComfyOutlined
                      className="thin"
                      sx={{ fontSize: "16px !important", ml: 0.5 }}
                    />
                  }
                  ref={layoutAnchorRef}
                  sx={{
                    border: "1px solid #e0e0e0",
                    fontWeight: activeLayoutId ? 600 : 400,
                    textTransform: "none",
                    px: 1.2,
                    borderRadius: "8px",
                  }}
                  size="medium"
                  color={activeLayoutId ? "primary" : "gray"}
                  endIcon={<KeyboardArrowDown />}
                  onClick={() => layoutMenuRef.current?.toggle()}
                  variant="outlined"
                >
                  {activeLayoutName
                    ? `Layout: ${activeLayoutName}`
                    : null || "Layout: Default"}
                </Button>
              </Badge>
              <StyledMenu
                ref={layoutMenuRef}
                anchorRef={layoutAnchorRef}
                title="Table Layouts"
                description="Create and manage your preferred table layouts."
                minWidth={300}
                maxWidth={
                  isEmpty(premadeLayouts) && isEmpty(userCreatedLayouts)
                    ? 180
                    : 420
                }
              >
                <Flx ac jb g={1} sx={{ p: 1.5 }}>
                  <Button
                    size="large"
                    variant="outlined"
                    color="primary"
                    sx={{ flexGrow: 1 }}
                    startIcon={<AddRounded fontSize="small" />}
                    onClick={handleSaveNewLayout}
                  >
                    Save Current Table As Layout
                  </Button>
                </Flx>
                <Divider />

                {/* ── Premade Templates Section ── */}
                {size(premadeLayouts) > 0 && (
                  <Box sx={{ py: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        px: 2,
                        pt: 0.5,
                        pb: 0.5,
                      }}
                    >
                      Templates
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                      }}
                    >
                      {premadeLayouts.map((layout) => {
                        const isActive = activeLayoutId === layout.id;
                        const isDefaultLayout = defaultId === layout.id;
                        return (
                          <MenuItem
                            key={layout.id}
                            sx={{
                              py: 0.75,
                              px: 1,
                              mx: 0.75,
                              borderRadius: "8px",
                              "&:hover .layout-actions": { opacity: 1 },
                            }}
                            onClick={() => handleApplyLayout(layout)}
                            selected={isActive}
                          >
                            <ListItemText
                              primary={
                                <Flx ac g={0.75} sx={{ mr: 2 }}>
                                  <Radio
                                    checked={isActive}
                                    size="small"
                                    sx={{
                                      p: 0,
                                      color: "#d1d5db",
                                      "&.Mui-checked": { color: "#2563eb" },
                                    }}
                                    disableRipple
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: isActive ? 600 : 400,
                                    }}
                                  >
                                    {layout.name}
                                  </Typography>
                                  <Chip
                                    label="Template"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      color: "#6b7280",
                                      borderColor: "#d1d5db",
                                      "& .MuiChip-label": {
                                        px: 0.75,
                                        fontSize: "10px",
                                        fontStyle: "italic",
                                      },
                                    }}
                                  />
                                  {isDefaultLayout && (
                                    <Chip
                                      label="Default"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{
                                        height: 20,
                                        "& .MuiChip-label": {
                                          px: 0.75,
                                          fontStyle: "italic",
                                          fontSize: "11px",
                                        },
                                      }}
                                    />
                                  )}
                                </Flx>
                              }
                            />
                            <Flx
                              ac
                              className="layout-actions"
                              sx={{
                                opacity: isActive || isDefaultLayout ? 1 : 0,
                                transition: "opacity 0.15s",
                                ml: 1,
                              }}
                            >
                              <Tooltip
                                title={
                                  isDefaultLayout
                                    ? "Remove as default"
                                    : "Set as default"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDefault(layout.id);
                                  }}
                                  sx={{ p: 0.5 }}
                                >
                                  {isDefaultLayout ? (
                                    <StarRounded
                                      sx={{ fontSize: 18, color: "#f59e0b" }}
                                    />
                                  ) : (
                                    <StarOutlineRounded
                                      sx={{ fontSize: 18, color: "#9ca3af" }}
                                    />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {/* No overwrite, rename, or delete for premade layouts */}
                            </Flx>
                          </MenuItem>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* ── Separator between premade and user layouts ── */}
                {size(premadeLayouts) > 0 && size(userCreatedLayouts) > 0 && (
                  <Divider />
                )}

                {/* ── User-Created Layouts Section ── */}
                {size(userCreatedLayouts) === 0 &&
                size(premadeLayouts) === 0 ? (
                  <Box sx={{ px: 2, py: 2, pt: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: 12, fontStyle: "italic" }}
                    >
                      No layouts created yet.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                    }}
                  >
                    {size(userCreatedLayouts) > 0 && (
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          px: 2,
                          pt: 0.5,
                          pb: 0.5,
                        }}
                      >
                        My Layouts
                      </Typography>
                    )}
                    {userCreatedLayouts.map((layout) => {
                      const isActive = activeLayoutId === layout.id;
                      const isDefaultLayout = defaultId === layout.id;
                      const hasFilters =
                        layout.filters &&
                        (layout.filters.statusFilter ||
                          layout.filters.subStatusFilter ||
                          layout.filters.quickFilter ||
                          layout.filters.activeView !== "all");
                      return (
                        <MenuItem
                          key={layout.id}
                          sx={{
                            py: 0.75,
                            px: 1,
                            mx: 0.75,
                            borderRadius: "8px",
                            "&:hover .layout-actions": { opacity: 1 },
                          }}
                          onClick={() => handleApplyLayout(layout)}
                          selected={isActive}
                        >
                          <ListItemText
                            primary={
                              <Flx ac g={0.75} sx={{ mr: 2 }}>
                                <Radio
                                  checked={isActive}
                                  size="small"
                                  sx={{
                                    p: 0,
                                    color: "#d1d5db",
                                    "&.Mui-checked": { color: "#2563eb" },
                                  }}
                                  disableRipple
                                />
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: isActive ? 600 : 400,
                                  }}
                                >
                                  {layout.name}
                                </Typography>
                                {isDefaultLayout && (
                                  <Chip
                                    label="Default"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      "& .MuiChip-label": {
                                        px: 0.75,
                                        fontStyle: "italic",
                                        fontSize: "11px",
                                      },
                                    }}
                                  />
                                )}
                                {hasFilters && (
                                  <Chip
                                    icon={
                                      <FilterAltOutlined
                                        sx={{ fontSize: "12px !important" }}
                                      />
                                    }
                                    label="Filters"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      color: "#6b7280",
                                      borderColor: "#d1d5db",
                                      "& .MuiChip-label": {
                                        px: 0.5,
                                        fontSize: "10px",
                                      },
                                      "& .MuiChip-icon": {
                                        ml: 0.5,
                                      },
                                    }}
                                  />
                                )}
                              </Flx>
                            }
                          />
                          <Flx
                            ac
                            className="layout-actions"
                            sx={{
                              opacity: isActive || isDefaultLayout ? 1 : 0,
                              transition: "opacity 0.15s",
                              ml: 1,
                            }}
                          >
                            <Tooltip
                              title={
                                isDefaultLayout
                                  ? "Remove as default"
                                  : "Set as default"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDefault(layout.id);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                {isDefaultLayout ? (
                                  <StarRounded
                                    sx={{ fontSize: 18, color: "#f59e0b" }}
                                  />
                                ) : (
                                  <StarOutlineRounded
                                    sx={{ fontSize: 18, color: "#9ca3af" }}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Overwrite with current table configuration">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateLayout(layout.id);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <SaveOutlined
                                  sx={{ fontSize: 16, color: "#6b7280" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Rename">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRenameLayout(layout);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <EditRounded
                                  sx={{ fontSize: 16, color: "#6b7280" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete layout">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteLayout(layout.id);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteOutlineRounded
                                  sx={{ fontSize: 16, color: "#ef4444" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Flx>
                        </MenuItem>
                      );
                    })}
                    {activeLayoutName && (
                      <>
                        <Divider sx={{ mt: 1 }} />
                        <MenuItem
                          sx={{
                            py: 0.75,
                            px: 1,
                            mx: 0.75,
                            borderRadius: "8px",
                          }}
                          onClick={handleClearActiveLayout}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{ fontSize: "12px", fontWeight: 400 }}
                              >
                                Reset to default
                              </Typography>
                            }
                          />
                        </MenuItem>
                      </>
                    )}
                  </Box>
                )}

                {/* ── Remember where I left off ── */}
                <Divider />
                <Box sx={{ px: 1.5, py: 1.25 }}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      alignItems: "flex-start",
                      gap: 1,
                      ".MuiFormControlLabel-label": { mt: 0.25 },
                    }}
                    control={
                      <Switch
                        size="small"
                        checked={rememberSession}
                        onChange={handleToggleRememberSession}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                          Remember table view
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                          Saves your current table view so it opens exactly as
                          you left it.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </StyledMenu>
              <DownloadDropdownButton />
            </Flx>
          </Flx>
        </Box>

        {/* AG Grid Table — wrapped so the slow-load prompt can overlay just
            below AG Grid's centered loading spinner while a fetch is in flight. */}
        <Box sx={{ position: "relative", height: "100%", minHeight: 0 }}>
          <LoanOfficerPipelineTable
            gridRef={gridRef}
            loading={loading}
            onCellClicked={onCellClicked}
            quickFilter={quickFilter}
            rowData={filteredPipelineLoansByStatus}
            onGridReady={handleGridReady}
            onStateUpdated={persistSessionNow}
            isCompact={isCompact}
          />
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: "calc(50% + 34px)",
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              <Box sx={{ pointerEvents: "auto" }}>
                <SlowRequestPrompt
                  context="your loan pipeline"
                  details={{ Request: "GET /loportal/user_pipeline" }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </ScreenContent>

      {/* Save / Rename Dialog */}
      <BasicModal
        title={
          saveDialogMode === "create"
            ? "Create New Layout"
            : "Rename Existing Layout"
        }
        description={
          saveDialogMode === "create"
            ? "Save your current layout configuration as a new layout."
            : null
        }
        show={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        bodySx={{ pt: 1.5 }}
      >
        <>
          <InputWrapper label="Layout Name">
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={saveDialogName}
              onChange={(e) => setSaveDialogName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveDialogConfirm();
              }}
            />
          </InputWrapper>
          <Flx end g={1} sx={{ mt: 2 }}>
            <Button
              size="large"
              onClick={() => setSaveDialogOpen(false)}
              color="inherit"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              size="large"
              onClick={handleSaveDialogConfirm}
              variant="contained"
              disabled={!saveDialogName.trim()}
            >
              {saveDialogMode === "create" ? "Save" : "Rename"}
            </Button>
          </Flx>
        </>
      </BasicModal>
    </Box>
  );
};

const PipelineStatusFilterSwitcher = ({
  statusFilter,
  setStatusFilter,
  getStatusGroupCount,
  loans,
  subStatusContent,
  fontSize = 12,
  sx,
}) => {
  const statusTabs = useMemo(
    () => [
      { label: "All", value: null, count: size(loans) },
      ...LOAN_STATUS_OPTIONS.map((status) => ({
        label: status,
        value: status,
        count: getStatusGroupCount(status),
      })),
    ],
    [loans, getStatusGroupCount],
  );

  return (
    <Flx
      wrap
      sx={{
        flexGrow: 1,
      }}
    >
      {/* <Flx ac sx={{ mt: 1.3, height: "max-content", p: 0.1, mr: 1.2 }}>
        <FilterAltOutlined
          className="thin"
          sx={{ fontSize: 15, color: "rgba(0, 0, 0, 0.6)" }}
        />
        <Txt
          secondary
          sx={{ fontSize: fontSize, ml: 0.5, flexShrink: 0, fontWeight: 400 }}
        >
          Loan Status:
        </Txt>
      </Flx> */}
      <Flx column>
        <Box
          sx={{
            // border: "1px solid #
            ...sx,
          }}
        >
          <Flx ac>
            <Flx ac sx={{ height: "max-content", p: 0.1, mr: 1.2 }}>
              <FilterAltOutlined
                className="thin"
                sx={{ fontSize: 15, color: "rgba(0, 0, 0, 0.6)" }}
              />
              <Txt
                secondary
                sx={{
                  fontSize: fontSize,
                  ml: 0.5,
                  flexShrink: 0,
                  fontWeight: 600,
                  color: "#000000d6",
                }}
              >
                Status:
              </Txt>
            </Flx>
            <Switcher
              activeColor={level1.text}
              value={statusFilter}
              fontSize={fontSize}
              fontWeight={400}
              activeFontWeight={500}
              activeBorderBottomDisable
              buttonPadding={{ px: 1, py: 0.75 }}
              onChange={setStatusFilter}
              tabs={statusTabs}
              sx={sx}
            />
          </Flx>
        </Box>
        {subStatusContent}
      </Flx>
    </Flx>
  );
};

// ─── Generic Sub-Status Switcher ─────────────────────────────────────────────

const SubStatusSwitcher = ({
  loans,
  statusFilter,
  subStatusFilter,
  setSubStatusFilter,
  sx,
  fontSize = 11.5,
}) => {
  const subStatuses = LOAN_SUBSTATUS_MAP[statusFilter];
  if (!statusFilter || !subStatuses) return null;

  const noSubStatusCount = countLoansForSubStatus(
    loans,
    statusFilter,
    SUB_FILTER_NONE,
  );
  const allCount = countLoansForSubStatus(loans, statusFilter, null);

  const subTabs = [
    { label: `All`, value: null, count: allCount },
    {
      label: `${statusFilter} (Unassigned)`,
      value: SUB_FILTER_NONE,
      count: noSubStatusCount,
    },
    ...subStatuses.map((sub) => ({
      label: sub,
      value: sub,
      count: countLoansForSubStatus(loans, statusFilter, sub),
    })),
  ];

  return (
    <Flx ac sx={sx}>
      <SubdirectoryArrowRight sx={{ fontSize: 12 }} />
      <Txt
        secondary
        sx={{ mr: 1, flexShrink: 0, fontStyle: "italic", fontSize }}
        ml={0.5}
        mb={0}
      >
        {statusFilter} Sub-Status:
      </Txt>
      <Switcher
        fontSize={fontSize}
        buttonPadding={{ px: 1, py: 0.75 }}
        value={subStatusFilter}
        activeBorderRadius={0}
        activeFontWeight={500}
        activeBackgroundColor={"transparent"}
        activeBorderColor="transparent"
        onChange={setSubStatusFilter}
        tabs={subTabs}
      />
    </Flx>
  );
};

const Switcher = ({
  tabs,
  onChange,
  value,
  sx,
  column = false,
  buttonPadding = { px: 1.75, py: 0.75 },
  fontSize = 14,
  backgroundColor,
  activeBackgroundColor = level1.tint,
  activeColor = level1.text,
  activeFontWeight = 700,
  activeBorderColor = level1.border,
  activeBorderRadius = "4px",
  activeBorderBottomDisable = false,
  color,
  ...rest
}) => {
  return (
    <FilterTabSwitcher
      fontSize={fontSize}
      value={value}
      activeBackgroundColor={activeBackgroundColor}
      buttonPadding={buttonPadding}
      inactiveCountColor={grey[600]}
      backgroundColor={"transparent"}
      activeColor={activeColor || level1.text}
      fontWeight={400}
      color={color || "#181d1f"}
      activeFontWeight={activeFontWeight}
      sx={{
        flexWrap: "wrap",
        flexGrow: 1,
        gap: 0,
        p: 0,
        ".selected": {
          boxShadow: "none",
          border: `1px solid ${activeBorderColor}`,
          borderBottom: activeBorderBottomDisable
            ? `1px solid ${activeBorderColor}`
            : "2px solid #1a56db",
          borderRadius: activeBorderRadius,
        },
        ...sx,
      }}
      onChange={onChange}
      tabs={tabs}
      {...rest}
    />
  );
};
// ─── AG Grid Table ────────────────────────────────────────────────────────────

export const LoanOfficerPipelineTable = ({
  gridRef,
  rowData,
  onRowClicked,
  quickFilter,
  loading,
  onCellClicked,
  onGridReady,
  onStateUpdated,
  isCompact,
  // Optional override so reusable consumers (e.g. PipelineTableScreen) can
  // supply their own columns. Defaults to the standard pipeline columns below.
  columnDefs: columnDefsOverride,
  // Optional per-consumer widening of the Loan Name column (without forking the
  // shared column defs). Used by PipelineTableScreen (Pre-Close / Closing).
  loanNameMinWidth,
  // Drilldown route base for the Loan # "open in new tab" link. Preclose loans
  // live at /loan/:id; acquisitions (postclose) at /acquisition/:id. Only used
  // by the default columns below — a `columnDefs` override supplies its own.
  // Rows tagged with `_pipelineKey` (mixed boards) resolve their own base.
  drilldownBase = "/loan",
  // Extra columns spliced in right after Loan # (e.g. the Full Pipeline's
  // Pipeline column + acquisition trade fields), without forking the defaults.
  extraColumnDefs,
}) => {
  const [showGrandTotal, setShowGrandTotal] = useState(false);

  const { isResfin } = useEnv();
  const onColumnRowGroupChanged = useCallback((params) => {
    const hasGroups = params.api.getRowGroupColumns().length > 0;
    setShowGrandTotal(hasGroups);
  }, []);
  const defaultColumnDefs = useMemo(
    () => [
      { hide: true, headerName: "_id", field: "_id" },
      {
        headerName: "Loan #",
        field: "loanNumber",
        pinned: "left",
        minWidth: 90,
        maxWidth: 90,
        width: 90,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        flex: 0,
        getQuickFilterText: (params) =>
          params.value ? String(params.value) : "",
        filter: "agTextColumnFilter",
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return (
            <OpenInNewTabButton
              linkIcon
              to={`${drilldownBaseForRow(params?.data, drilldownBase)}/${params?.data?._id}`}
            >
              {params?.data?.loanNumber}
            </OpenInNewTabButton>
          );
        },
        suppressFloatingFilterButton: true,
      },
      {
        field: "sync_loan_id",
        headerName: "Loan Sync ID",
        hide: true,
        width: 150,
        minWidth: 150,
        flex: 0,
      },
      { headerName: "Asset ID", field: "assetId", hide: true },
      {
        headerName: "Created",
        field: "createDateTime",
        type: "date",
        // createDateTime is a UTC *timestamp* (has time-of-day), not a date-only
        // value. The `date` type's formatter assumes UTC-midnight date-only and
        // reads UTC components, which rolls evening timestamps forward a day in
        // Eastern. Format via formatDateTime (UTC → Eastern) instead.
        valueFormatter: (p) => formatDateTime(p?.value, "date"),
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        wrapHeaderText: true,
        cellRenderer: (p) => {
          if (p.node.group) return null;
          return (
            <CellRendererStackedChip
              icon={<HourglassEmptyOutlined className="thin-8" />}
              params={p}
              compact={p?.context?.isCompact}
            />
          );
        },
      },
      {
        headerName: "Updated",
        field: "updateDateTime",
        type: "date",
        // See createDateTime above — UTC timestamp, format via UTC → Eastern.
        valueFormatter: (p) => formatDateTime(p?.value, "date"),
        minWidth: 115,
        maxWidth: 115,
        width: 115,
        wrapHeaderText: true,
        suppressHeaderFilterButton: true,
        cellRenderer: (p) => {
          if (p.node.group) return null;
          return (
            <CellRendererStackedChip
              icon={<UpdateRounded className="thin-8" />}
              params={p}
              compact={p?.context?.isCompact}
            />
          );
        },
      },
      {
        headerName: "Loan Name",
        field: "loanName",
        minWidth: 280,
        width: 360,
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return (
            <CellRendererLoanName
              {...params}
              compact={params?.context?.isCompact}
            />
          );
        },
        suppressSizeToFit: true,
        valueGetter: (params) =>
          isNil(params?.data?.loanName)
            ? `Loan #${params?.data?.loanNumber}`
            : params?.data?.loanName,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        // autoHeight stays on; in compact the renderer emits a single line so
        // rows shrink naturally (no columnDefs change → layouts preserved).
        autoHeight: true,
        suppressFloatingFilterButton: true,
        headerTooltip: "Go to loan",
        cellStyle: { cursor: "pointer" },
      },
      {
        headerName: "Comments",
        field: "comments",
        suppressAutoSize: true,
        suppressSizeToFit: true,
        suppressHeaderFilterButton: true,
        width: DEFAULT_COMMENTS_WIDTH,
        // Min wide enough that the compact count chip + date don't wrap.
        minWidth: COMPACT_COMMENTS_WIDTH,
        flex: 0,
        // autoHeight stays on; compact content is a single line so rows shrink.
        autoHeight: true,
        wrapText: true,
        suppressFloatingFilterButton: true,
        // cellStyle is a function so it can read compact from grid context
        // (re-evaluated on refreshCells) without rebuilding columnDefs.
        cellStyle: (params) =>
          params?.context?.isCompact
            ? {
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft: 0,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }
            : {
                paddingTop: "14px",
                paddingBottom: "14px",
                paddingLeft: 0,
                cursor: "pointer",
              },
        cellRenderer: (params) => {
          if (params.node.group) return null;
          return (
            <CellRendererLoanComments
              {...params}
              compact={params?.context?.isCompact}
            />
          );
        },

        cellClass: (params) => {
          if (isEmpty(params?.value)) return "ag-cell-vertical-center";
        },
        comparator: (valueA, valueB) => {
          const hasA = valueA && Array.isArray(valueA) && valueA.length > 0;
          const hasB = valueB && Array.isArray(valueB) && valueB.length > 0;
          if (!hasA && !hasB) return 0;
          if (!hasA) return -1;
          if (!hasB) return 1;
          return (
            new Date(valueA[0].commentDateTime).getTime() -
            new Date(valueB[0].commentDateTime).getTime()
          );
        },
      },
      {
        headerName: "Expected Closing Date",
        field: "expectedClosingDate",
        type: "date",
        flex: 0,
        width: 180,
        cellStyle: { cursor: "pointer", display: "flex", alignItems: "center" },
        // The closing date sits in a chip (like the Comments cell) whose color
        // flags overdue loans — instead of tinting the whole cell. The editable
        // pencil still appears on row hover, and a tooltip explains the color.
        cellRenderer: (params) => {
          if (params.node.group) return null;
          const editable = <CellRendererEditableLoanField {...params} />;
          if (!params?.value) return editable;
          const isPastDate = isBefore(new Date(params.value), new Date());
          const isClosedLoan = [
            "Closed",
            "Closed w/ Post Closing Conditions",
            "Balance Sheet",
            "Loan Sold",
            "Inactive",
            "Dead",
          ].includes(params?.data?.loanStatus);
          const overdue = isPastDate && !isClosedLoan;
          const closedPast = isPastDate && isClosedLoan;
          const tooltip = overdue
            ? "Expected closing date has passed and the loan is not yet closed"
            : closedPast
              ? "Loan closed on or after its expected closing date"
              : "Expected closing date is upcoming";
          return (
            <Tooltip title={tooltip} placement="top-start">
              <span className="closing-date-chip">
                <ChipItem
                  customContent={editable}
                  backgroundColor={
                    overdue ? red[50] : closedPast ? green[50] : "#fff"
                  }
                  sx={{
                    color: overdue
                      ? red[800]
                      : closedPast
                        ? green[800]
                        : undefined,
                    ".MuiButton-root": {
                      px: 0.5,
                      py: 0,
                      minWidth: 0,
                      fontSize: "inherit",
                      // lineHeight: 1.4,
                    },
                    ".ag-editable-edit-icon": { color: grey[700] },
                  }}
                />
              </span>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Closing Cutoff Date",
        field: "closingCutoffDate",
        type: "date",
        hide: isResfin,
        valueFormatter: (params) => {
          if (isNil(params.value)) return "Not Set";
          return params.value;
        },
        cellRenderer: (params) => {
          if (params.node.group) return null;

          return <CellRendererEditableLoanField {...params} />;
        },
      },
      {
        headerName: "Originator",
        field: "originator",
        flex: 0,
        width: 115,
        minWidth: 115,
      },
      {
        headerName: "Loan Product Type",
        field: "loanProductType",
        flex: 0,
        width: 155,
        minWidth: 120,
      },
      { headerName: "Loan Purpose", field: "loanPurpose", flex: 0, width: 140 },
      {
        headerName: "Loan Status",
        field: "loanStatus",
        width: 140,
        minWidth: 120,
        cellStyle: { cursor: "pointer", display: "flex", alignItems: "center" },
        flex: 0,
        // When the Sub Status column is hidden, fold the sub-status into this
        // cell as "Loan Status > Sub Status"; otherwise show just the status.
        // The value stays a single string so sort / filter / export keep working
        // on it; the renderer below is what stacks the two onto separate lines.
        valueGetter: (params) => {
          const loanStatus = params?.data?.loanStatus;
          if (!loanStatus) return loanStatus;
          const subStatus = params?.data?.subStatus;
          const subCol = params?.api?.getColumn?.("subStatus");
          const subHidden = subCol ? !subCol.isVisible() : true;
          return subHidden && subStatus
            ? `${loanStatus} > ${subStatus}`
            : loanStatus;
        },
        // Status on top, sub-status beneath it in a smaller muted line — the
        // inline "Status > Sub Status" form got long enough to truncate at this
        // column width. Group / total rows fall through to the plain value.
        cellRenderer: (params) => {
          if (params?.node?.group || !params?.data)
            return params?.value ?? null;
          const loanStatus = params.data.loanStatus;
          if (!loanStatus) return params?.value ?? null;
          const subStatus = params.data.subStatus;
          const subCol = params?.api?.getColumn?.("subStatus");
          const subHidden = subCol ? !subCol.isVisible() : true;
          const showSub = subHidden && Boolean(subStatus);
          return (
            <Flx column sx={{ justifyContent: "center", minWidth: 0 }}>
              <Box
                sx={{
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {loanStatus}
              </Box>
              {showSub && (
                <Box
                  sx={{
                    fontSize: "11px",
                    color: "#64748b",
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {subStatus}
                </Box>
              )}
            </Flx>
          );
        },
      },
      {
        // When the loan entered its current loanStatus (the LOS "last stage
        // change"). A UTC timestamp from the backend; the cell shows the days
        // since (Eastern calendar days), the dateTime type keeps sort/filter/export
        // on the date. Null on loans whose status moved without a history entry.
        headerName: "Current Status Start Date",
        field: "currentStatusStartDate",
        type: "dateTime",
        cellRenderer: CellRendererStatusStartDate,
        filterParams: statusStartDateFilterParams,
        flex: 0,
        width: 210,
        minWidth: 140,
        wrapHeaderText: true,
      },
      {
        headerName: "Sub Status",
        field: "subStatus",
        // Hidden by default (folds into the Loan Status cell as
        // "Loan Status > Sub Status"); stays here next to Loan Status in the
        // column order so toggling it on slots it back into place.
        hide: true,
        flex: 0,
        width: 120,
        minWidth: 100,
        cellStyle: { display: "flex", alignItems: "center" },
      },

      {
        headerName: "Borrower Entity",
        // The boards are fed by two different pipeline projections, which name
        // the computed entity differently (`entityName` on /underwriting/
        // underwriting_pipeline, `borrowerEntityName` on /loportal/user_pipeline)
        // — and older loans only have the legacy standalone `borrowerEntity`.
        // getLoanBorrowerEntityName covers all three plus the entities-first
        // `entities[]` array, so the column isn't all "N/A" on whichever
        // endpoint happens to feed the board.
        field: "entityName",
        valueGetter: (p) => getLoanBorrowerEntityName(p?.data),
        //
      },
      // Lendmarq divergence (see CLAUDE.md #1): Eastview's "Account Manager"
      // (accountManager) column becomes "Loan Officer" (salesperson), and the
      // "Structurer" column is dropped — LQ's middle role is Processor, which is
      // already its own column below. Do not let a sync re-add the EV roles.
      {
        headerName: "Loan Officer",
        field: "salesperson",
        cellRenderer: CellRendererRoleUser,
        flex: 0,
        width: 120,
        minWidth: 80,
      },
      {
        headerName: "Underwriter",
        field: "underwriter",
        cellRenderer: CellRendererRoleUser,
        flex: 0,
        width: 120,
        minWidth: 80,
      },
      {
        headerName: "Loan Closer",
        field: "loanCloser",
        cellRenderer: CellRendererRoleUser,
        flex: 0,
        width: 120,
        minWidth: 80,
      },
      {
        headerName: "Sales Analyst",
        field: "salesAnalyst",
        cellRenderer: CellRendererRoleUser,
        flex: 0,
        width: 120,
        minWidth: 80,
      },
      {
        headerName: "Processor",
        field: "processor",
        cellRenderer: CellRendererRoleUser,
        flex: 0,
        width: 120,
        minWidth: 80,
      },
      // Lendmarq divergence (see CLAUDE.md #1): EV's Pre-UW / QC assignee columns
      // (preUnderwriter "Junior Underwriter" / qc_analyst "QC Review") are EV-only
      // workflow stages and are NOT brought into Lendmarq. A sync re-adds them; drop them.
      {
        headerName: "Broker",
        colId: "brokerContact",
        field: "loanRelatedContacts",
        flex: 0,
        width: 150,
        minWidth: 100,
        // String array of broker names so set/text filters and quick-filter work;
        // the renderer reads the raw contacts for the rich hover.
        valueGetter: (p) =>
          getLoanRelatedContacts(p?.data, "Broker Contact").map(
            formatContactName,
          ),
        cellRenderer: (p) => (
          <CellRendererLoanRelatedContacts
            params={p}
            roleFilter="Broker Contact"
          />
        ),
      },
      {
        headerName: "Loan Related Contacts",
        field: "loanRelatedContacts",
        hide: true,
        minWidth: 200,
        // "Name (Role)" strings for filtering/quick-filter; renderer shows the
        // full details on hover.
        valueGetter: (p) =>
          getLoanRelatedContacts(p?.data).map((c) =>
            c?.loanRole
              ? `${formatContactName(c)} (${c.loanRole})`
              : formatContactName(c),
          ),
        cellRenderer: (p) => (
          <CellRendererLoanRelatedContacts params={p} showRole />
        ),
      },
      // ------- All default not shown below (Sorted A->Z) -------
      {
        headerName: "Base Loan Amount",
        field: "baseLoanAmount",
        type: "dollar",
        cellRenderer: CellRendererEditableLoanField,
        hide: true,
      },
      { field: "baseLoanToAsIsValue", type: "percent", hide: true },
      { field: "baseLoanToCost", type: "percent", hide: true },
      { field: "baseLoanToPurchasePrice", type: "percent", hide: true },
      {
        headerName: "Borrowers",
        field: "borrowers",
        hide: true,
        valueGetter: (params) => {
          const v = params.data?.borrowers;
          if (isEmpty(v) || isNil(v)) return;
          return v.map((item) =>
            isEmpty(item) || isNil(item?.firstName) || isNil(item?.lastName)
              ? "N/A"
              : `${item.firstName} ${item.lastName}`,
          );
        },
      },
      { field: "closingMonth", hide: true },
      { field: "closingQuarter", hide: true },
      {
        headerName: "Closing Scheduled",
        field: "closingScheduled",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Closing Scheduled Date",
        field: "closingScheduledDate",
        type: "boolean",
        hide: true,
      },
      { field: "closingWeek", headerName: "Closing Week", hide: true },
      {
        headerName: "Created By",
        field: "createdBy",
        hide: true,
        cellRenderer: CellRendererRoleUser,
        flex: 0,
        width: 120,
        minWidth: 80,
      },
      {
        headerName: "Credit Authorizations",
        field: "creditAuthorizations",
        hide: true,
      },
      {
        headerName: "Days In Current Status",
        field: "daysInCurrentStatus",
        type: "number",
        hide: true,
      },
      {
        headerName: "Discount Points",
        field: "discountPoints",
        type: "percent",
        hide: true,
      },
      { headerName: "Dutch Interest", field: "dutchInterest", hide: true },
      {
        headerName: "Exceptions",
        field: "exceptions",
        hide: true,
        minWidth: 205,
        width: 225,
        flex: 0,
        // Summary text for export / quick-filter; sort ranks by open count, then total.
        valueGetter: (p) => {
          const c = countExceptionBuckets(loanExceptions(p?.data));
          return `${c.open} Open · ${c.approved} Approved · ${c.other} Other`;
        },
        // Text filter matches the statuses present ("approved", "in review", …).
        filterValueGetter: (p) =>
          [
            ...new Set(
              loanExceptions(p?.data).map(
                (e) =>
                  `${exceptionStatusOf(e)} ${EXCEPTION_BUCKETS.find((b) => b.key === exceptionBucketOf(e))?.label}`,
              ),
            ),
          ].join(" | "),
        comparator: (_a, _b, nodeA, nodeB) => {
          const a = countExceptionBuckets(loanExceptions(nodeA?.data));
          const b = countExceptionBuckets(loanExceptions(nodeB?.data));
          return (
            a.open - b.open || a.other - b.other || a.approved - b.approved
          );
        },
        cellRenderer: CellRendererExceptions,
      },
      {
        headerName: "Existing Loan In Default",
        field: "existingLoanInDefault",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Funded Date",
        field: "fundedDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Interest Rate",
        field: "interestRate",
        type: "percent",
        hide: true,
      },
      {
        headerName: "Is Balance Sheet Closing",
        field: "isBalanceSheetClosing",
        hide: true,
      },
      {
        headerName: "Is Table Funding",
        field: "isTableFunding",
        type: "boolean",
        hide: true,
      },
      {
        headerName: "Last Viewed",
        field: "lastViewDateTime",
        type: "date",
        minWidth: 200,
        hide: true,
      },
      { headerName: "Loan Documents", field: "loanDocuments", hide: true },
      {
        headerName: "Maturity Date",
        field: "maturityDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Milestone History",
        field: "milestoneStatusChanges",
        hide: true,
        minWidth: 220,
        width: 260,
        flex: 0,
        // Readable "Prior → Updated" transition strings for filter/quick-filter;
        // the renderer shows the latest transition with the full timeline on hover.
        valueGetter: (p) =>
          (Array.isArray(p?.data?.milestoneStatusChanges)
            ? p.data.milestoneStatusChanges
            : []
          ).map(milestoneTransitionLabel),
        cellRenderer: CellRendererMilestoneHistory,
      },
      {
        headerName: "Net Funding",
        field: "netFunding",
        type: "dollar",
        hide: true,
      },
      { field: "openConditionsCount", hide: true },
      { field: "openExceptionsCount", hide: true },
      {
        headerName: "Origination Date",
        field: "originationDate",
        type: "date",
        hide: true,
      },
      {
        headerName: "Origination Points",
        field: "originationPoints",
        type: "percent",
        hide: true,
      },
      { headerName: "Permitted Users", field: "permittedUsers", hide: true },
      {
        headerName: "Pipeline",
        field: "pipeline",
        width: 140,
        flex: 0,
        hide: true,
      },
      {
        headerName: "Referral Source",
        field: "referralSource",
        cellRenderer: CellRendererEditableLoanField,
        valueFormatter: (params) => params?.data?.referralSource || "None",
        hide: true,
      },
      {
        headerName: "Subject Properties",
        field: "subjectProperties",
        hide: true,
        valueGetter: (params) => {
          const v = params.data?.subjectProperties;
          if (isEmpty(v) || isNil(v)) return;
          return v.map((item) =>
            isEmpty(item) || isNil(addressLabel(item?.address))
              ? "N/A"
              : `${addressLabel(item.address)}`,
          );
        },
      },
      { field: "totalArv", type: "dollar", hide: true },
      { field: "totalAsIsValue", type: "dollar", hide: true },
      { field: "totalAssignmentFees", type: "dollar", hide: true },
      { field: "totalConstructionBudget", type: "dollar", hide: true },
      { field: "totalCostBasis", type: "dollar", hide: true },
      {
        headerName: "Total Holdback",
        field: "totalHoldback",
        type: "dollar",
        hide: true,
      },
      { field: "totalInterestReserves", type: "percent", hide: true },
      {
        headerName: "Total Loan Amount",
        field: "totalLoanAmount",
        type: "dollar",
        hide: true,
      },
      { field: "totalLoanToArv", type: "percent", hide: true },
      { field: "totalPurchasePrice", type: "dollar", hide: true },
      {
        headerName: "JSON",
        colId: "json",
        hide: true,
        width: 110,
        minWidth: 90,
        flex: 0,
        sortable: false,
        filter: false,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        enableRowGroup: false,
        enablePivot: false,
        enableValue: false,
        valueGetter: () => "",
        cellRenderer: CellRendererJsonButton,
      },
    ],
    [isResfin, drilldownBase],
  );

  const baseColumnDefs = useMemo(() => {
    const base = columnDefsOverride ?? defaultColumnDefs;
    if (isEmpty(extraColumnDefs)) return base;
    const at = base.findIndex((c) => c?.field === "loanNumber") + 1;
    return [...base.slice(0, at), ...extraColumnDefs, ...base.slice(at)];
  }, [columnDefsOverride, defaultColumnDefs, extraColumnDefs]);
  const columnDefs = useMemo(() => {
    if (!loanNameMinWidth) return baseColumnDefs;
    return baseColumnDefs.map((c) =>
      c?.field === "loanName" ? { ...c, minWidth: loanNameMinWidth } : c,
    );
  }, [baseColumnDefs, loanNameMinWidth]);

  const getRowId = useCallback((params) => params.data._id, []);
  const getRowStyle = useCallback((params) => {
    if (params.node.footer) {
      return { fontWeight: "bold" };
    }
    return null;
  }, []);
  const defaultColDef = useMemo(
    () => ({
      cursor: "pointer",
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      showRowGroup: false,
      sortable: true,
      flex: 1,
      resizable: true,
      cellClass: "ag-cell-vertical-center",
      minWidth: 150,
      // minWidth: 200,
      floatingFilter: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      filter: "agMultiColumnFilter",
      filterParams: {},
    }),
    [],
  );

  // Compact toggles via grid context (not by rebuilding columnDefs, which would
  // wipe an applied custom layout). Re-render the cells, recompute autoHeight
  // row heights, and nudge the Comments column width — applyColumnState with a
  // single column leaves the rest of the user's layout (order/visibility/other
  // widths) untouched.
  const applyCompactCommentsWidth = useCallback(
    (api) => {
      if (!api) return;
      api.applyColumnState({
        state: [
          {
            colId: "comments",
            width: isCompact ? COMPACT_COMMENTS_WIDTH : DEFAULT_COMMENTS_WIDTH,
          },
        ],
      });
    },
    [isCompact],
  );

  useEffect(() => {
    const api = gridRef?.current?.api;
    if (!api) return;
    applyCompactCommentsWidth(api);
    api.refreshCells({ force: true });
    api.resetRowHeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompact]);

  // The mount effect above can run before the grid API exists, so also apply
  // the compact width once the grid is ready (after any saved layout). Deferred
  // a tick so it lands after the layout application on ready.
  const handleGridReady = useCallback(
    (params) => {
      onGridReady?.(params);
      setTimeout(() => {
        if (!params?.api || params.api.isDestroyed?.()) return;
        applyCompactCommentsWidth(params.api);
        params.api.resetRowHeights();
      }, 0);
    },
    [onGridReady, applyCompactCommentsWidth],
  );

  const handleOnRowClicked = useCallback(
    (params) => {
      if (params.node.group) {
        params.node.setExpanded(!params.node.expanded);
        return;
      }
      onRowClicked?.(params?.data);
    },
    [onRowClicked],
  );

  const columnTypes = useMemo(
    () => ({
      ...columnTypesDate(),
      ...columnTypesNumber(),
      ...columnTypesDollar(),
    }),
    [],
  );
  const sideBar = useMemo(() => ({ toolPanels: ["columns", "filters"] }), []);
  // The board's own pipeline, read by orderStatusRowGroups to pick which
  // status ladder orders the Loan Status groups.
  const pipelineKey = useMemo(
    () =>
      Object.values(PIPELINES).find((p) => p.drilldownBase === drilldownBase)
        ?.key || PIPELINE_KEYS.UNDERWRITING,
    [drilldownBase],
  );
  const gridContext = useMemo(
    () => ({ isCompact, pipelineKey }),
    [isCompact, pipelineKey],
  );

  return (
    <Box
      sx={{
        borderRadius: "0px",
        height: "100%",
        ".ag-theme-quartz .ag-root-wrapper": {
          borderLeft: "none !important",
          borderRight: "none !important",
          borderBottom: "none !important",
          borderRadius: "0px !important",
        },
      }}
    >
      <AgGridTableWrapper height="100%" compactTable>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          columnTypes={columnTypes}
          getRowId={getRowId}
          quickFilterText={quickFilter}
          defaultColDef={defaultColDef}
          sideBar={sideBar}
          getRowStyle={getRowStyle}
          grandTotalRow={showGrandTotal ? "bottom" : undefined}
          onModelUpdated={redrawGrandTotalRow}
          onColumnRowGroupChanged={onColumnRowGroupChanged}
          onColumnVisible={(p) => {
            // Re-render Loan Status when Sub Status visibility changes so it can
            // fold/unfold the sub-status into the cell.
            if (p?.column?.getColId?.() === "subStatus") {
              p.api.refreshCells({ columns: ["loanStatus"], force: true });
            }
          }}
          onCellClicked={onCellClicked}
          loading={loading}
          groupDisplayType={"singleColumn"}
          autoGroupColumnDef={PIPELINE_AUTO_GROUP_COLUMN_DEF}
          // "Drag here to set row groups" bar above the header — drag any
          // column in to group by it (every column is enableRowGroup via
          // defaultColDef). Dropping a header outside the grid no longer
          // hides the column, so a missed drop into the bar is harmless.
          rowGroupPanelShow="always"
          suppressDragLeaveHidesColumns
          initialGroupOrderComparator={orderStatusRowGroups}
          onRowClicked={handleOnRowClicked}
          onGridReady={handleGridReady}
          onStateUpdated={onStateUpdated}
          context={gridContext}
          tooltipShowDelay={100}
          headerHeight={54}
          floatingFiltersHeight={40}
          enableCellTextSelection
        />
      </AgGridTableWrapper>
    </Box>
  );
};

const MyLoansCheckbox = ({ activeView, onViewChange, assignedCount }) => {
  const isChecked = activeView === "assigned";
  return (
    <ButtonBase
      disableRipple
      onClick={() => onViewChange(isChecked ? "all" : "assigned")}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        userSelect: "none",
        borderRadius: "6px",
        px: 1,
        py: 1,
        border: "1px solid transparent",
        "&:hover": { bgcolor: "#f0f4f8" },
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: "4px",
          border: isChecked ? "2px solid #1a56db" : "2px solid #d1d5db",
          bgcolor: isChecked ? "#1a56db" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.12s",
        }}
      >
        {isChecked && (
          <Box component="svg" viewBox="0 0 12 12" sx={{ width: 9, height: 9 }}>
            <path
              d="M2 6l3 3 5-5"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </Box>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          color: "#374151",
          whiteSpace: "nowrap",
          lineHeight: 1,
        }}
      >
        Show only my loans
        <Typography
          component="span"
          sx={{ fontSize: 12, color: "#94a3b8", pl: 0.4, lineHeight: 1 }}
        >
          ({assignedCount})
        </Typography>
      </Typography>
    </ButtonBase>
  );
};

export const DownloadDropdownButton = () => {
  const { loading, downloadStageDurationReport, downloadPipeline } =
    useLoanFilesHook();

  const downloadAnchorRef = useRef(null);
  const downloadMenuRef = useRef(null);

  const handleDownload = useCallback(
    (type) => {
      downloadMenuRef.current?.close();
      if (type === "stage-duration") {
        downloadStageDurationReport({ fileDownloadName: "Pipeline Files" });
      } else if (type === "pipeline-zip") {
        downloadPipeline({ fileDownloadName: "Pipeline Files" });
      }
    },
    [downloadStageDurationReport, downloadPipeline],
  );

  return (
    <>
      <Button
        ref={downloadAnchorRef}
        onClick={() => downloadMenuRef.current?.toggle()}
        size="medium"
        variant="outlined"
        color="gray"
        loading={loading}
        endIcon={<KeyboardArrowDown />}
        sx={{
          border: "1px solid #e0e0e0",
          textTransform: "none",
          px: 1.2,
          borderRadius: "8px",
        }}
      >
        Export
      </Button>
      <StyledMenu
        ref={downloadMenuRef}
        anchorRef={downloadAnchorRef}
        title="Export Options"
        minWidth={260}
      >
        <Box sx={{ py: 0.5 }}>
          <MenuItem
            sx={{ py: 1, px: 1.5, mx: 0.75, borderRadius: "8px" }}
            onClick={() => handleDownload("stage-duration")}
          >
            <ListItemText
              primary={
                <Flx ac g={1}>
                  <DownloadRounded sx={{ fontSize: 16, color: "#6b7280" }} />
                  <Typography sx={{ fontSize: 12 }}>
                    Export Stage Duration Report to Excel
                  </Typography>
                </Flx>
              }
            />
          </MenuItem>
          <MenuItem
            sx={{ py: 1, px: 1.5, mx: 0.75, borderRadius: "8px" }}
            onClick={() => handleDownload("pipeline-zip")}
          >
            <ListItemText
              primary={
                <Flx ac g={1}>
                  <DownloadOutlined sx={{ fontSize: 16, color: "#6b7280" }} />
                  <Typography sx={{ fontSize: 12 }}>Export To Excel</Typography>
                </Flx>
              }
            />
          </MenuItem>
        </Box>
      </StyledMenu>
    </>
  );
};
export default LoanOfficerPipelineScreen;
